import * as React from "react";
import * as _ from "underscore";
import * as moment from "moment";
import { Button, Modal as CarbonModal, Toggle } from "carbon-components-react";
import { Add16, Link16, Save16, TrashCan16 } from "@carbon/icons-react";
import {
  Modal,
  Panel,
  Table,
  ButtonToolbar,
  OverlayTrigger,
  DropdownButton,
  Tooltip,
  MenuItem,
} from "react-bootstrap";
import { FormattedDate } from "react-intl";
import { connect } from "react-redux";

import APIClient from "web_modules/APIClient";
import DocEntry from "web_modules/underwriter_dashboard/shared/DocEntry";
import EditableField from "web_modules/underwriter_dashboard/shared/EditableField";
import MaskedAccountNumber from "web_modules/underwriter_dashboard/shared/MaskedAccountNumber";
import {
  RateAdjusters,
  rateAdjusterToTooltip,
  rateAdjusterToLabel,
  rateAdjusterToRank,
  CreditReportResidence,
  OFACCheck,
  ConsumerStatement,
  FraudAlert,
  BorrowerEntity,
  LoanApplicationBorrowers,
  LoanApplicationBorrower,
  CreditReport,
  AppShape,
  Tradeline,
} from "@eave/shared/types/api";
import { addressToStr } from "web_modules/underwriter_dashboard/shared";
import { ExplanationStatus } from "web_modules/Explanations";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import LearnMore from "web_modules/shared/LearnMore";
import { HurdleStatus } from "web_modules/underwriter_dashboard/HurdleStatus";
import { HurdleProps } from "underwriter_dashboard/HurdleEditor";
import NotificationsForTradeIn from "underwriter_dashboard/hurdle_editors/CreditSummary/NotificationsForTradeIn";
import NotificationsForNetEquity from "underwriter_dashboard/hurdle_editors/CreditSummary/NotificationsForNetEquity";
import Money from "../../../Money";
import { ExplanationTransactions, ExplanationsHurdleEditor } from "../Explanations";
import { mapDispatchToProps } from "../selectors";
import OrderNewCreditReport from "underwriter_dashboard/hurdle_editors/CreditSummary/OrderNewCreditReport";
import BorrowerEntityUtil, {
  getAssociatedBorrowers,
  getBorrowerEntitiesAsStrings,
} from "@eave/shared/utils/borrowers";
import Borrower from "@eave/borrower/web_modules/Unconfirmed/shared/Borrowers/Borrower";
import CreditSummaryTools from "underwriter_dashboard/hurdle_editors/CreditSummary/creditSummaryUtilities";

const styles = require("underwriter_dashboard/hurdle_editors/styles.module.scss");

require(`assets/stylesheets/components/underwriter_dashboard/hurdle_editors/credit_summary.scss`);

const uuid = require("uuid");
const humanize = require("underscore.string/humanize");

interface HurdleEditorProps extends HurdleProps {
  dispatch?: Function;
  loadApp: (appID: number) => void;
}

const TRADELINE_ACCOUNT_TYPE_OPTIONS = [
  { label: "Collections, Judgements, and Liens", value: "CollectionsJudgementsAndLiens" },
  { label: "HELOC", value: "HELOC" },
  { label: "Installment", value: "Installment" },
  { label: "Lease Payments", value: "LeasePayments" },
  { label: "Mortgage", value: "Mortgage" },
  { label: "Open", value: "Open" },
  { label: "Other Liability", value: "OtherLiability" },
  { label: "Revolving", value: "Revolving" },
  { label: "Taxes", value: "Taxes" },
];

const CATEGORY_OPTIONS = [
  { label: "Alimony", value: "Alimony" },
  { label: "Automobile", value: "Automobile" },
  { label: "Child Support", value: "ChildSupport" },
  { label: "Collection", value: "Collection" },
  { label: "Credit Card", value: "CreditCard" },
  { label: "Educational", value: "Educational" },
  { label: "Garnishment", value: "Garnishment" },
  { label: "Job Related Expense", value: "JobRelatedExpense" },
  { label: "Judgment / Lien", value: "JudgementLien" },
  { label: "Line of Credit", value: "CreditLine" },
  { label: "Loan", value: "Loan" },
  { label: "Medical", value: "Medical" },
  { label: "Open Account / Chargecard", value: "OpenAccountChargecard" },
  { label: "Other", value: "Other" },
  { label: "Other Expenses", value: "OtherExpenses" },
  { label: "Separate Maintenance", value: "SeparateMaintenanceExpense" },
];

const MORTGAGE_TYPE_OPTIONS = [
  { label: "Conventional Mortgage", value: "ConventionalRealEstateMortgage" },
  { label: "USDA Rural Development Mortgage", value: "FarmersHomeAdministrationFHMA" },
  { label: "FHA Real Estate Mortgage", value: "FHARealEstateMortgage" },
  { label: "Other", value: "Other" },
  { label: "Veterans Administration Mortgage", value: "VeteransAdministrationRealEstateMortgage" },
];

const OWNERSHIP_OPTIONS = [
  { label: "Pending Sale", value: "IsBeingSold" },
  { label: "Subject Property", value: "SubjectProperty" },
  { label: "Sold", value: "Sold" },
  { label: "Retained as Primary", value: "PrincipalResidence" },
  { label: "Retained as Second", value: "VacationSecondHome" },
  { label: "Retained as Vacant Lot", value: "VacantLand" },
  { label: "Rental Investment", value: "RentedOut" },
  { label: "Other Investment", value: "OtherInvestment" },
  { label: "Fixing & Flipping", value: "IsBeingFixedAndFlipped" },
];

const INQUIRY_INDUSTRY_OPTIONS = [
  { label: "Educational", value: "Educational" },
  { label: "Automobile", value: "Automobile" },
  { label: "RealEstate", value: "RealEstate" },
  { label: "ChargeAccount", value: "ChargeAccount" },
  { label: "Recreational/StoreCredit", value: "Recreational/StoreCredit" },
  { label: "CreditCard", value: "CreditCard" },
  { label: "Other", value: "Other" },
];

const YES_NO_OPTIONS = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

const ESCROWED_OPTIONS = [
  { label: "Escrowed", value: true },
  { label: "Not Escrowed", value: false },
];

const NOT_INCLUDED_OPTIONS = [
  { label: "Duplicate", value: "Duplicate" },
  {
    label: "Open 30-day Account, Balance deducted from assets",
    value: "Open 30-day Account, Balance deducted from assets",
  },
  { label: "Authorized User", value: "Authorized User" },
  { label: "Paid By Business", value: "Paid By Business" },
  { label: "Paid By Another", value: "Paid By Another" },
  { label: "Error in Reporting", value: "Error in Reporting" },
  { label: "Paid in Full", value: "Paid in Full" },
  { label: "To Be Paid", value: "To Be Paid" },
];

// const MORTGAGE_TRADELINE_STATUS_OPTIONS = NOT_INCLUDED_OPTIONS.concat([
//   { label: "To Be Subordinated", value: "ToBeSubordinated"}, // REFI only, enable this lat er
//   { label: "To Be Subordinated with Paydown ",
//   value: "ToBeSubordinatedWithPaydown"}, // REFI only, enable this later
// ]);

const BORROWER_ENTITY_OPTIONS = [
  { label: "B1", value: 1 },
  { label: "B2", value: 2 },
  { label: "B1, B2", value: 3 },
];

const deepClone = (thing: any) => JSON.parse(JSON.stringify(thing));

const renderAdjusterTooltip = (adj: RateAdjusters) => {
  const text = rateAdjusterToTooltip(adj);
  if (text) {
    return <LearnMore key={adj} iconTheme="grey" text={text} />;
  }
  return null;
};

const renderBorrowerEntity = (
  borrowerEntity: number | BorrowerEntity,
  borrowers: LoanApplicationBorrowers,
): JSX.Element => {
  const associatedBorrowers = getAssociatedBorrowers(borrowerEntity, borrowers);
  const borrowerEntities = associatedBorrowers.map((borrower: LoanApplicationBorrower) => {
    switch (borrower.borrower_entity) {
      case 1:
        return "B1";
      case 2:
        return "B2";
      case 4:
        return "B3";
      case 8:
        return "B4";
    }
    return borrower.borrower_entity;
  });
  return <span>{borrowerEntities.join(", ")}</span>;
};

export class CreditSummaryHurdleEditor extends React.Component<HurdleEditorProps, any> {
  constructor(props: HurdleEditorProps) {
    super(props);
    this.state = {
      tradelines:
        (props.app.credit_reports.length > 0 &&
          CreditSummaryHurdleEditor.sortTradelines(
            _.flatten(props.app.credit_reports.map(cr => cr.tradelines)),
          )) ||
        [],
      inquiries:
        (props.app.credit_reports.length > 0 &&
          CreditSummaryHurdleEditor.sortInquiries(
            _.flatten(props.app.credit_reports.map(cr => cr.inquiries)),
          )) ||
        [],
      ownedRealEstate: deepClone(props.app.owned_real_estate),
      additionalLiabilities: deepClone(props.app.additional_debts),
      explanations: props.app.explanations || [],
      viewingDocumentID: null,
      highlightedTradelines: [],
      filtersOn: true,
      showDebtExplanations: false,
      // TODO only good for this load until we figure out linkage of loe/liability
      debtExplanationIDsWithNewLiabilities: [],
      inquiryIDsWithNewExplanations: [],

      // UI State
      deleteModalIsOpen: false,
    };
  }

  static sortTradelines(tradelines) {
    const halfSorted = _.sortBy(tradelines, (tradeline: any) => tradeline.opened_date);

    halfSorted.reverse();

    return _.sortBy(halfSorted, (tradeline: any) => {
      const acctStatus = tradeline.account_status.toLowerCase();
      if (acctStatus === "open") {
        return 1;
      }
      if (acctStatus === "paid") {
        return 2;
      }
      if (acctStatus === "closed") {
        return 3;
      }
      return 4;
    });
  }

  static sortInquiries(inquiries) {
    const sortedInquiries = _.sortBy(inquiries, (inquiry: any) => inquiry.date);
    sortedInquiries.reverse();
    return sortedInquiries;
  }

  reloadApp() {
    this.props.loadApp(this.props.app.id);
  }

  static updateInquiries(inquiries) {
    return APIClient.post("/inquiries", { inquiries });
  }

  updateTradelines(tradelines) {
    return APIClient.post(`/${this.props.app.id}/tradelines`, { tradelines });
  }

  static updateOwnedRealEstate(ownedRealEstate) {
    return APIClient.put("/uw/owned_real_estate", { reos: ownedRealEstate });
  }

  static updateAdditionalDebt(additionalDebts) {
    return APIClient.put("/additional_debts", { additional_debts: additionalDebts });
  }

  deleteAdditionalDebt(additionalDebtId) {
    return APIClient.deleteRequest(`/${this.props.app.id}/additional_debts/${additionalDebtId}`);
  }

  deleteOwnedRealEstate(ownedRealEstateId) {
    return APIClient.deleteRequest(`/uw/owned_real_estate/${ownedRealEstateId}`);
  }

  // helper fn to replace ids which are uuid strings w/ 0, so the backend can create new entries
  // (when new items are created, we create a temporary uuid in this hurdle to allow editing
  // values before saving since items are keyed on their pk ids, but the backend is expecting
  // an int when saving so we have to clear the uuid first).
  static clearIdsIfString(list) {
    return list.map(item => {
      if (typeof item.id === "string") {
        return { ...item, id: 0 };
      }
      return item;
    });
  }

  onSave() {
    const { inquiries, tradelines, ownedRealEstate, additionalLiabilities } = this.state;

    let updatedRealEstate;
    let updatedLiabilities;

    Promise.all([
      CreditSummaryHurdleEditor.updateInquiries(inquiries),
      this.updateTradelines(tradelines),
    ])
      .then(() => {
        updatedRealEstate = CreditSummaryHurdleEditor.clearIdsIfString(ownedRealEstate);
      })
      .then(() => CreditSummaryHurdleEditor.updateOwnedRealEstate(updatedRealEstate))
      .then(() => {
        updatedLiabilities = CreditSummaryHurdleEditor.clearIdsIfString(additionalLiabilities);
      })
      .then(() => CreditSummaryHurdleEditor.updateAdditionalDebt(updatedLiabilities))
      .then(() => {
        const toDelete = [];
        _.each(this.props.app.additional_debts, debt => {
          if (this.findIndexOfLiabilityWithID(debt.id) < 0) {
            toDelete.push(this.deleteAdditionalDebt(debt.id));
          }
        });
        return Promise.all(toDelete);
      })
      .then(() => {
        const toDelete = [];
        _.each(this.props.app.owned_real_estate, ownedRealEstate => {
          if (this.findIndexOfOwnedRealEstateWithID(ownedRealEstate.id) < 0) {
            toDelete.push(this.deleteOwnedRealEstate(ownedRealEstate.id));
          }
        });
        return Promise.all(toDelete);
      })
      .then(() => {
        this.reloadApp();
      });
  }

  createExplanation(inquiryID, explanationMessage) {
    const { app } = this.props;

    APIClient.post("/explanations", {
      loan_application_id: app.id,
      type: "Inquiry",
      status: ExplanationStatus.needs_response,
      message: explanationMessage,
    }).then(() => {
      this.state.inquiryIDsWithNewExplanations.push(inquiryID);
      this.setState({
        inquiryIDsWithNewExplanations: this.state.inquiryIDsWithNewExplanations,
      });
    });
  }

  handleOwnedRealEstateTradelineChange(tradelineID, field) {
    const index = this.findIndexOfTradelineWithID(tradelineID);
    const newState = this.state;
    newState.tradelines[index][field.name] = field.value;
    if (field.name === "operator_not_included_reason") {
      newState.tradelines[index].operator_include_in_ratios = field.value === null;
    }
    this.setState(newState);
  }

  handleTradelineChange(tradelineID, field) {
    const index = this.findIndexOfTradelineWithID(tradelineID);
    const newState = this.state;
    newState.tradelines[index][field.name] = field.value;
    this.setState(newState);
  }

  handleInquiryChange(inquiryID, field) {
    const index = this.findIndexOfInquiryWithID(inquiryID);
    const newState = this.state;
    newState.inquiries[index][field.name] = field.value;
    this.setState(newState);
  }

  handleOwnedRealEstateChange(ownedRealEstateID, field) {
    const index = this.findIndexOfOwnedRealEstateWithID(ownedRealEstateID);
    const newState = this.state;
    newState.ownedRealEstate[index][field.name] = field.value;
    this.setState(newState);
  }

  handleLiabilityChange(liabilityID, field) {
    const index = this.findIndexOfLiabilityWithID(liabilityID);
    const newState = this.state;
    newState.additionalLiabilities[index][field.name] = field.value;
    this.setState(newState);
  }

  handleOwnedRealEstateLiabilityChange(liabilityID, field) {
    const index = this.findIndexOfLiabilityWithID(liabilityID);
    const newState = this.state;
    newState.additionalLiabilities[index][field.name] = field.value;
    if (field.name === "operator_not_included_reason") {
      newState.additionalLiabilities[index].operator_include_in_ratios = field.value === null;
    }
    this.setState(newState);
  }

  deleteLiability(liabilityID) {
    const index = this.findIndexOfLiabilityWithID(liabilityID);
    const newState = this.state;
    newState.additionalLiabilities.splice(index, 1);
    this.setState(newState);
  }

  openDeleteModal(realEstateID) {
    this.setState({ deleteModalIsOpen: true, realEstateMarkedForDeletion: realEstateID });
  }

  closeDeleteModal() {
    this.setState({ deleteModalIsOpen: false, realEstateMarkedForDeletion: null });
  }

  deleteRealEstate(ownedRealEstateID) {
    const index = this.findIndexOfOwnedRealEstateWithID(ownedRealEstateID);
    const newORE = [...this.state.ownedRealEstate];
    newORE.splice(index, 1);

    const newState = {
      ...this.state,
      deleteModalIsOpen: false,
      realEstateMarkedForDeletion: null,
      ownedRealEstate: newORE,
    };
    this.setState(newState);
  }

  linkLoEsToRealEstate(realEstateID, ev) {
    ev.stopPropagation();
    this.setState({ showingLoERealEstateLinker: realEstateID });
  }

  hideLoERealEstateLinker() {
    this.setState({ showingLoERealEstateLinker: false });
  }

  linkTradelinesToRealEstate(ownedRealEstateID) {
    this.setState({ showingTradelineLinker: ownedRealEstateID });
  }

  hideTradelineLinker() {
    this.setState({ showingTradelineLinker: false });
  }

  linkLoEsToTradeline(tradelineID, ev) {
    ev.stopPropagation();
    this.setState({ showingLoETradelineLinker: tradelineID });
  }

  hideLoETradelineLinker() {
    this.setState({ showingLoETradelineLinker: false });
  }

  toggleDebtLoEs() {
    this.setState({ showDebtExplanations: !this.state.showDebtExplanations });
  }

  handleLoERealEstateLink(ownedRealEstateID, explanationID, ev) {
    const ownedRealEstateIndex = this.findIndexOfOwnedRealEstateWithID(ownedRealEstateID);

    const newState = this.state;

    let explanationIDs = newState.ownedRealEstate[ownedRealEstateIndex].explanation_ids;

    if (ev.target.checked) {
      explanationIDs = explanationIDs.concat(explanationID);
    } else {
      const explanationIndex = _.findIndex(explanationIDs, {
        id: explanationID,
      });
      explanationIDs.splice(explanationIndex, 1);
    }
    newState.ownedRealEstate[ownedRealEstateIndex].explanation_ids = explanationIDs;
    this.setState(newState);
  }

  handleLoELink(tradelineID, explanationID, ev) {
    const tradelineIndex = this.findIndexOfTradelineWithID(tradelineID);

    const newState = this.state;

    let explanationIDs = newState.tradelines[tradelineIndex].explanation_ids;

    if (ev.target.checked) {
      explanationIDs = explanationIDs.concat(explanationID);
    } else {
      const explanationIndex = _.findIndex(explanationIDs, {
        id: explanationID,
      });
      explanationIDs.splice(explanationIndex, 1);
    }

    newState.tradelines[tradelineIndex].explanation_ids = explanationIDs;
    this.setState(newState);
  }

  handleAdditionalLiabilityLink(ownedRealEstateID, additionalLiabilityID, ev) {
    const liabilityIndex = this.findIndexOfLiabilityWithID(additionalLiabilityID);
    const additionalLiability = this.state.additionalLiabilities[liabilityIndex];
    const ownedRealEstateIndex = this.findIndexOfOwnedRealEstateWithID(ownedRealEstateID);

    const newState = this.state;

    if (ev.target.checked) {
      newState.additionalLiabilities[liabilityIndex].owned_real_estate_id = ownedRealEstateID;
      newState.ownedRealEstate[ownedRealEstateIndex].additional_liability_ids =
        newState.ownedRealEstate[ownedRealEstateIndex].additional_liability_ids || [];
      newState.ownedRealEstate[ownedRealEstateIndex].additional_liability_ids.push(
        additionalLiability.id,
      );
    } else {
      const tradelineIDs =
        newState.ownedRealEstate[ownedRealEstateIndex].additional_liability_ids || [];
      const associatedTradelineIndex = tradelineIDs.indexOf(additionalLiabilityID);
      tradelineIDs.splice(associatedTradelineIndex, 1);
      newState.ownedRealEstate[ownedRealEstateIndex].additional_liability_ids = tradelineIDs;
      newState.additionalLiabilities[liabilityIndex].owned_real_estate_id = null;
    }
    this.setState(newState);
  }

  handleTradelineLink(ownedRealEstateID, tradelineID, ev) {
    const tradelineIndex = this.findIndexOfTradelineWithID(tradelineID);
    const tradeline = this.state.tradelines[tradelineIndex];
    const ownedRealEstateIndex = this.findIndexOfOwnedRealEstateWithID(ownedRealEstateID);

    const newState = this.state;

    if (ev.target.checked) {
      newState.tradelines[tradelineIndex].owned_real_estate_id = ownedRealEstateID;
      newState.ownedRealEstate[ownedRealEstateIndex].tradeline_ids =
        newState.ownedRealEstate[ownedRealEstateIndex].tradeline_ids || [];
      newState.ownedRealEstate[ownedRealEstateIndex].tradeline_ids.push(tradeline.id);
    } else {
      const tradelineIDs = newState.ownedRealEstate[ownedRealEstateIndex].tradeline_ids || [];
      const associatedTradelineIndex = tradelineIDs.indexOf(tradelineID);
      tradelineIDs.splice(associatedTradelineIndex, 1);
      newState.ownedRealEstate[ownedRealEstateIndex].tradeline_ids = tradelineIDs;
      newState.tradelines[tradelineIndex].owned_real_estate_id = null;
    }
    this.setState(newState);
  }

  findIndexOfOwnedRealEstateWithID(ownedRealEstateID) {
    return _.findIndex(this.state.ownedRealEstate, { id: ownedRealEstateID });
  }

  findIndexOfTradelineWithID(tradelineID) {
    return _.findIndex(this.state.tradelines, { id: tradelineID });
  }

  findIndexOfInquiryWithID(inquiryID) {
    return _.findIndex(this.state.inquiries, { id: inquiryID });
  }

  findIndexOfLiabilityWithID(liabilityID) {
    return _.findIndex(this.state.additionalLiabilities, { id: liabilityID });
  }

  static getIsMedical(tradeline) {
    if (tradeline.operator_is_medical_flag === true) {
      return true;
    }
    if (tradeline.operator_is_medical_flag === false) {
      return false;
    }
    return tradeline.is_medical;
  }

  static getIsDisputed(tradeline) {
    if (tradeline.operator_consumer_dispute_flag === true) {
      return true;
    }
    if (tradeline.operator_consumer_dispute_flag === false) {
      return false;
    }
    return !tradeline.include_in_ratios;
  }

  handleClick(tradelineIDs) {
    let oldHighlightedTradelines = this.state.highlightedTradelines;
    for (let i = 0; i < tradelineIDs.length; i++) {
      const tradelineID = tradelineIDs[i];
      const highlightIndex = oldHighlightedTradelines.indexOf(tradelineID);
      if (highlightIndex >= 0) {
        oldHighlightedTradelines.splice(highlightIndex, 1);
      } else {
        oldHighlightedTradelines = oldHighlightedTradelines.concat(tradelineID);
      }
    }
    this.setState({ highlightedTradelines: oldHighlightedTradelines });
  }

  static getAssociatedBorrowers(borrowerIdentifier) {
    const borrowerID = borrowerIdentifier.toString().toLowerCase();
    if (borrowerID === "borrec000001") {
      return "B1";
    }
    if (borrowerID === "borrec000002") {
      return "B2";
    }
    if (borrowerID === "borrec000001 borrec000002") {
      return "B1, B2";
    }
    if (borrowerID === "1") {
      return "B1";
    }
    if (borrowerID === "2") {
      return "B2";
    }
    if (borrowerID === "3") {
      return "B1, B2";
      // the rest of these are to handle non-standard language &
      // formatting from creditreportfixtures
    }
    if (borrowerID === "borrower1") {
      return "B1";
    }
    if (borrowerID === "borrower 1") {
      return "B1";
    }
    if (borrowerID === "borrower2") {
      return "B2";
    }
    if (borrowerID === "borrower 2") {
      return "B2";
    }
    if (borrowerID === "borrower 1, borrower2") {
      return "B1, B2";
    }
    if (borrowerID === "borrower 1, borrower 2") {
      return "B1, B2";
    }
    if (borrowerID === "borrower1, borrower 2") {
      return "B1, B2";
    }
    if (borrowerID === "borrower1, borrower2") {
      return "B1, B2";
    }
    if (borrowerID === "borrower1,borrower2") {
      return "B1, B2";
    }
    return borrowerIdentifier;
  }

  toggleFilter() {
    this.setState(prevState => ({ filtersOn: !prevState.filtersOn }));
  }

  renderOpenLiabilities() {
    const { tradelines, filtersOn } = this.state;
    const { app } = this.props;

    const isActiveTradeline = tradeline => {
      if (tradeline.is_real_estate && tradeline.owned_real_estate_id) {
        return false; // Filter out real estate that have been linked to an Owned Real Estate
      }

      // Is active if the currently monthly balance or monthly payment amount is not 0
      return (
        parseInt(tradeline.current_balance_amount, 10) !== 0 ||
        parseInt(tradeline.monthly_payment_amount, 10) !== 0
      );
    };

    let totalMonthlyLiabilities = 0;
    let totalMonthlyLiabilitiesExcludedFromDTI = 0;

    return (
      <div className="credit_summary__table_container">
        <div
          style={{ display: "flex", marginTop: "20px", marginBottom: "10px", alignItems: "center" }}
        >
          <h2 style={{ marginRight: "10px", position: "relative", top: "5px" }}>Tradelines</h2>
          <Toggle
            toggled={filtersOn}
            onToggle={this.toggleFilter.bind(this)}
            labelA="Showing all tradelines"
            labelB="Showing active tradelines"
          />
        </div>
        <p>
          Front DTI: {(Number(app.decision.dti_front) * 100).toFixed(2)}% <br />
        </p>
        <p>
          Back DTI: {(Number(app.decision.dti) * 100).toFixed(2)}% <br />
          {Number(app.decision.dti) > 0.43 && (
            <span>
              Do NOT use messaging feature for this. Call Borrower to walk through Liabilities and
              their responsibilities to figure out strategies to get below 43% DTI.
            </span>
          )}
        </p>
        <Table className="credit_summary__open_liabilities__table">
          <thead>
            <tr>
              <th>Include in Ratios</th>
              <th>Creditor Name</th>
              <th>Acct #</th>
              <th>Acct Type</th>
              <th>Category</th>
              <th>Monthly Payment</th>
              <th>Balance</th>
              <th>Remaining Payments</th>
              <th>Associated Borrower(s)</th>
              <th>Associated Txns</th>
              <th>Docs</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {tradelines &&
              tradelines
                .filter(tl => !filtersOn || isActiveTradeline(tl))
                .map((tradeline, index) => {
                  const calculatedMonthlyPaymentAmount =
                    Number(tradeline.monthly_payment_amount) === 0
                      ? tradeline.calculated_monthly_payment
                      : tradeline.monthly_payment_amount;
                  const monthlyPaymentAmount = Number(
                    tradeline.operator_monthly_payment_amount || calculatedMonthlyPaymentAmount,
                  );
                  const remainingPayments = Math.ceil(
                    (tradeline.operator_balance || tradeline.current_balance_amount) /
                      monthlyPaymentAmount,
                  );
                  const includeInRatios = !(
                    tradeline.operator_include_in_ratios === false ||
                    (tradeline.operator_include_in_ratios !== true &&
                      tradeline.include_in_ratios === false)
                  );
                  const rowClassName = includeInRatios
                    ? "credit_summary__row"
                    : "credit_summary__row--faded";
                  if (!includeInRatios) {
                    totalMonthlyLiabilitiesExcludedFromDTI += monthlyPaymentAmount;
                  } else {
                    totalMonthlyLiabilities += monthlyPaymentAmount;
                  }
                  const hoverClassName =
                    this.state.highlightedTradelines.indexOf(tradeline.id) >= 0
                      ? "credit_summary__row--hover"
                      : "credit_summary_row";

                  return (
                    <tr
                      key={index}
                      className={`${rowClassName} ${hoverClassName}`}
                      onClick={() => this.handleClick([tradeline.id])}
                    >
                      <td>
                        <EditableField
                          inputType="selectBool"
                          inputName={`operator_include_in_ratios_${index}`}
                          fieldName="operator_include_in_ratios"
                          originalValue={tradeline.include_in_ratios}
                          changedValue={tradeline.operator_include_in_ratios}
                          options={YES_NO_OPTIONS}
                          handleOnChange={this.handleTradelineChange.bind(this, tradeline.id)}
                        />
                        {tradeline.include_in_ratios_reason && (
                          <LearnMore placement="right" text={tradeline.include_in_ratios_reason} />
                        )}
                        {tradeline.operator_include_in_ratios === false && (
                          <EditableField
                            inputType="select"
                            inputName={`operator_not_included_reason_${index}`}
                            fieldName="operator_not_included_reason"
                            originalValue=""
                            changedValue={tradeline.operator_not_included_reason}
                            options={NOT_INCLUDED_OPTIONS}
                            handleOnChange={this.handleTradelineChange.bind(this, tradeline.id)}
                            editing={!tradeline.operator_not_included_reason}
                            placeholder="Please select a reason"
                          />
                        )}
                      </td>
                      <td>{tradeline.creditor_name}</td>
                      <td>
                        <MaskedAccountNumber accountNumber={tradeline.account_number} />
                      </td>
                      <td>
                        <EditableField
                          inputType="select"
                          inputName={`operator_account_type_${index}`}
                          fieldName="operator_account_type"
                          originalValue={tradeline.account_type}
                          changedValue={tradeline.operator_account_type}
                          options={TRADELINE_ACCOUNT_TYPE_OPTIONS}
                          handleOnChange={this.handleTradelineChange.bind(this, tradeline.id)}
                        />
                      </td>
                      <td>
                        <EditableField
                          inputType="select"
                          inputName={`operator_category_${index}`}
                          fieldName="operator_category"
                          originalValue={tradeline.credit_loan_type}
                          changedValue={tradeline.operator_category}
                          options={CATEGORY_OPTIONS}
                          handleOnChange={this.handleTradelineChange.bind(this, tradeline.id)}
                        />
                      </td>
                      <td>
                        <EditableField
                          inputType="money"
                          inputName={`operator_monthly_payment_amount_${index}`}
                          fieldName="operator_monthly_payment_amount"
                          originalValue={
                            calculatedMonthlyPaymentAmount ===
                            tradeline.operator_monthly_payment_amount
                              ? tradeline.monthly_payment_amount
                              : calculatedMonthlyPaymentAmount
                          }
                          changedValue={tradeline.operator_monthly_payment_amount}
                          handleOnChange={this.handleTradelineChange.bind(this, tradeline.id)}
                        />
                        <LearnMore
                          text={`${tradeline.calculated_monthly_payment_source.amount} from ${tradeline.calculated_monthly_payment_source.source}`}
                        />
                      </td>
                      <td>
                        <EditableField
                          fieldName="operator_balance"
                          inputName={`operator_balance_${index}`}
                          inputType="money"
                          originalValue={tradeline.current_balance_amount}
                          changedValue={tradeline.operator_balance}
                          handleOnChange={this.handleTradelineChange.bind(this, tradeline.id)}
                        />
                      </td>
                      <td>{isNaN(remainingPayments) ? 0 : remainingPayments}</td>
                      <td>{renderBorrowerEntity(tradeline.borrower_entity, app.borrowers)}</td>
                      <td>
                        <a onClick={this.linkLoEsToTradeline.bind(this, tradeline.id)}>
                          {this.transactionsForTradeline(tradeline).length}
                        </a>
                      </td>
                      <td>-</td>
                      <td className="credit_summary__note_column">
                        <EditableField
                          fieldName="operator_note"
                          inputName={`operator_note_${index}`}
                          inputType="textarea"
                          originalValue=""
                          changedValue={
                            tradeline.operator_note ? tradeline.operator_note : undefined
                          }
                          handleOnChange={this.handleTradelineChange.bind(this, tradeline.id)}
                        />
                      </td>
                    </tr>
                  );
                })}
          </tbody>
          <tfoot>
            <tr className="credit_summary__total_row">
              <td colSpan={5} className="credit_summary__total_row__label">
                Total monthly liabilities from credit report:
              </td>
              <td>
                <Money value={totalMonthlyLiabilities} />
              </td>
              <td colSpan={6} />
            </tr>
            <tr className="credit_summary__total_row">
              <td colSpan={5} className="credit_summary__total_row__label">
                Total monthly liabilities excluded from DTI:
              </td>
              <td>
                <Money value={totalMonthlyLiabilitiesExcludedFromDTI} />
              </td>
              <td colSpan={6} />
            </tr>
          </tfoot>
        </Table>
      </div>
    );
  }

  renderCalculatedVariableSummary() {
    return (
      <div className="credit_summary__calculated_variables credit_summary__table_container">
        <h2>Credit Score &amp; Calculated Variables Summary</h2>
        {this.renderHeadlineMetrics()}
        {this.renderSeasoning()}
        {this.renderRiskLayeringCriteria()}
        {this.renderOtherKeyVariables()}
      </div>
    );
  }

  getScoresByBorrower(filterExpr = null) {
    let scores = _.flatten(this.props.app.credit_reports.map(cr => cr.credit_scores)) || [];
    if (filterExpr) {
      scores = _.filter(scores, filterExpr);
    }

    return _.chain(scores)
      .sortBy(s => s.score)
      .reverse() // highest to lowest
      .groupBy(s => s.borrower_entity)
      .values()
      .value();
  }

  static getCreditHistoryLengthFromTradeline(tradeline) {
    const tradelineOpenedDate = moment(tradeline?.opened_date);
    const tradelineCreatedDate = moment(tradeline?.created_at);

    return Math.round(
      moment.duration(tradelineCreatedDate.valueOf() - tradelineOpenedDate.valueOf()).asYears(),
    );
  }

  static isFirstTimeHomeBuyer(tradelines) {
    const realEstateTradelines = tradelines.filter(t => t.is_real_estate);

    return realEstateTradelines.length === 0;
  }

  renderHeadlineMetrics() {
    const scoresByBorrower = this.getScoresByBorrower();
    if (scoresByBorrower.length === 0) {
      return null;
    }

    const { tradelines } = this.state;
    const { borrowers } = this.props.app;
    const decisionCreditScore = this.props.app.decision.credit_score;
    const b1 = borrowers[BorrowerEntity.Borrower1];
    const b2 = borrowers[BorrowerEntity.Borrower2];
    const b3 = borrowers[BorrowerEntity.Borrower3];
    const b4 = borrowers[BorrowerEntity.Borrower4];
    const tradelinesByBorrower = CreditSummaryTools.getTradelinesByBorrower(tradelines);

    const b1CreditScore = b1.credit_score;
    const b2CreditScore = b2?.credit_score;
    const b3CreditScore = b3?.credit_score;
    const b4CreditScore = b4?.credit_score;
    const b1ClassName =
      b1CreditScore === decisionCreditScore ? "credit_summary__credit_score--bold" : "";
    const b2ClassName =
      b2CreditScore === decisionCreditScore ? "credit_summary__credit_score--bold" : "";
    const b3ClassName =
      b3CreditScore === decisionCreditScore ? "credit_summary__credit_score--bold" : "";
    const b4ClassName =
      b4CreditScore === decisionCreditScore ? "credit_summary__credit_score--bold" : "";

    return (
      <div
        className={`credit_summary__calculated_variables__headline_metrics \
credit_summary__table_container`}
      >
        <Panel>
          <Panel.Heading>Headline Metrics</Panel.Heading>
          <Panel.Body>
            <Table>
              <thead>
                <tr>
                  <th />
                  <th>Borrower 1</th>
                  {b2 && <th>Borrower 2</th>}
                  {b3 && <th>Borrower 3</th>}
                  {b4 && <th>Borrower 4</th>}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>FICO Score</td>
                  <td className={b1ClassName}>
                    <em>{b1CreditScore}</em>
                  </td>
                  {b2 && (
                    <td className={b2ClassName}>
                      <em>{b2CreditScore}</em>
                    </td>
                  )}
                  {b3 && (
                    <td className={b3ClassName}>
                      <em>{b3CreditScore}</em>
                    </td>
                  )}
                  {b4 && (
                    <td className={b4ClassName}>
                      <em>{b4CreditScore}</em>
                    </td>
                  )}
                </tr>
                <tr>
                  <td>Length of Credit History (Yrs)</td>
                  <td>
                    {tradelinesByBorrower[0]
                      ? CreditSummaryHurdleEditor.getCreditHistoryLengthFromTradeline(
                          tradelinesByBorrower[0][0],
                        )
                      : "? (no tradelines for B1)"}
                  </td>
                  {b2 && (
                    <td>
                      {tradelinesByBorrower[1]
                        ? CreditSummaryHurdleEditor.getCreditHistoryLengthFromTradeline(
                            tradelinesByBorrower[1][0],
                          )
                        : "? (no tradelines for B2)"}
                    </td>
                  )}
                  {b3 && (
                    <td>
                      {tradelinesByBorrower[2]
                        ? CreditSummaryHurdleEditor.getCreditHistoryLengthFromTradeline(
                            tradelinesByBorrower[2][0],
                          )
                        : "? (no tradelines for B3)"}
                    </td>
                  )}
                  {b4 && (
                    <td>
                      {tradelinesByBorrower[3]
                        ? CreditSummaryHurdleEditor.getCreditHistoryLengthFromTradeline(
                            tradelinesByBorrower[3][0],
                          )
                        : "? (no tradelines for B4)"}
                    </td>
                  )}
                </tr>
                <tr>
                  <td>First Time Home Buyer</td>
                  <td>
                    {tradelinesByBorrower[0]
                      ? CreditSummaryHurdleEditor.isFirstTimeHomeBuyer(tradelinesByBorrower[0])
                        ? "Y"
                        : "N"
                      : "? (no tradelines for B1)"}
                  </td>
                  {b2 && (
                    <td>
                      {tradelinesByBorrower[1]
                        ? CreditSummaryHurdleEditor.isFirstTimeHomeBuyer(tradelinesByBorrower[1])
                          ? "Y"
                          : "N"
                        : "? (no tradelines for B2)"}
                    </td>
                  )}
                  {b3 && (
                    <td>
                      {tradelinesByBorrower[2]
                        ? CreditSummaryHurdleEditor.isFirstTimeHomeBuyer(tradelinesByBorrower[2])
                          ? "Y"
                          : "N"
                        : "? (no tradelines for B3)"}
                    </td>
                  )}
                  {b4 && (
                    <td>
                      {tradelinesByBorrower[3]
                        ? CreditSummaryHurdleEditor.isFirstTimeHomeBuyer(tradelinesByBorrower[3])
                          ? "Y"
                          : "N"
                        : "? (no tradelines for B4)"}
                    </td>
                  )}
                </tr>
              </tbody>
            </Table>
          </Panel.Body>
        </Panel>
      </div>
    );
  }

  static getRateImpact(rbpWeights, triggerName) {
    const impact = rbpWeights[triggerName];
    if (impact === null) {
      return "TBD";
    }
    return impact;
  }

  renderRiskLayeringCriteria() {
    const rbpWeights = this.props.app.decision.rate.rate_adjusters;
    const sortedKeys = _.sortBy(Object.keys(rbpWeights), (k: RateAdjusters) =>
      rateAdjusterToRank(k),
    );

    return (
      <div
        className={`credit_summary__calculated_variables__risk_layering \
credit_summary__table_container`}
      >
        <Panel>
          <Panel.Heading>Risk Layering Criteria</Panel.Heading>
          <Panel.Body>
            <Table>
              <thead>
                <tr>
                  <th />
                  <th>Trig Wt</th>
                </tr>
              </thead>
              <tbody>
                {sortedKeys.map((k: RateAdjusters) => (
                  <tr key={k}>
                    <td>
                      {rateAdjusterToLabel(k)} {renderAdjusterTooltip(k)}
                    </td>
                    <td>{CreditSummaryHurdleEditor.getRateImpact(rbpWeights, k)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Panel.Body>
        </Panel>
      </div>
    );
  }

  renderOtherKeyVariables() {
    return (
      <div
        className={`credit_summary__calculated_variables__other_key_variables \
credit_summary__table_container`}
      >
        <Panel>
          <Panel.Heading>Other Key Variables</Panel.Heading>
          <Panel.Body>
            <Table>
              <thead>
                <tr>
                  <th />
                  <th />
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td># of public records other than BK/Real Estate related</td>
                  <td>{this.props.app.decision.total_number_public_records_non_bk_non_re}</td>
                </tr>
                <tr>
                  <td># of inquiries in the last 6 months</td>
                  <td>{this.props.app.decision.credit_inquiries}</td>
                </tr>
                <tr>
                  <td># of non-transactional tradelines opened in last 6 months</td>
                  <td>{this.props.app.decision.number_non_transactional_last_6}</td>
                </tr>
                <tr>
                  <td>Revolving Utilization%</td>
                  <td>
                    {(this.props.app.decision.percent_revolving_utilization * 100).toFixed(2)}%
                  </td>
                </tr>
                <tr>
                  <td># of small dollar collection tradelines currently open</td>
                  <td>{this.props.app.decision.number_small_dollar_collections}</td>
                </tr>
                <tr>
                  <td>Current Delinquencies</td>
                  <td>{this.props.app.decision.number_of_delinquents}</td>
                </tr>
                <tr>
                  <td>$ Balance on current delinquents</td>
                  <td>{this.props.app.decision.amount_of_delinquents}</td>
                </tr>
              </tbody>
            </Table>
          </Panel.Body>
        </Panel>
      </div>
    );
  }

  renderSeasoning() {
    return (
      <div
        className={`credit_summary__calculated_variables__seasoning \
credit_summary__table_container`}
      >
        <Panel>
          <Panel.Heading>Seasoning</Panel.Heading>
          <Panel.Body>
            <Table>
              <thead>
                <tr>
                  <th />
                  <th>BK</th>
                  <th>FCL</th>
                  <th>SS/DIL</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Time Since Last (Yrs)</td>
                  <td>
                    {Number(this.props.app.decision.years_since_last_bankruptcy) === 99
                      ? "-"
                      : this.props.app.decision.years_since_last_bankruptcy}
                  </td>
                  <td>
                    {Number(this.props.app.decision.years_since_foreclosure) === 99
                      ? "-"
                      : this.props.app.decision.years_since_foreclosure}
                  </td>
                  <td>
                    {Number(this.props.app.decision.years_since_short_sale) === 99
                      ? "-"
                      : this.props.app.decision.years_since_short_sale}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Panel.Body>
        </Panel>
      </div>
    );
  }

  static getPaymentHistorySlice(tradeline, periodEnd, periodStart) {
    const paymentHistoryStartDate = moment(tradeline.payment_history_start_date);

    if (paymentHistoryStartDate.isBefore(periodStart)) {
      return "";
    }
    if (paymentHistoryStartDate.isAfter(periodEnd)) {
      const offset = Math.round(
        moment.duration(paymentHistoryStartDate.valueOf() - periodEnd.valueOf()).asMonths(),
      );
      const monthsNeeded = Math.round(
        moment.duration(periodEnd.valueOf() - periodStart.valueOf()).asMonths(),
      );
      return tradeline.payment_history.substring(offset, offset + monthsNeeded);
    }
    if (paymentHistoryStartDate.isBefore(periodEnd)) {
      const monthsNeeded = Math.round(
        moment.duration(periodEnd.valueOf() - paymentHistoryStartDate.valueOf()).asMonths(),
      );
      return tradeline.payment_history.substring(0, monthsNeeded);
    }
  }

  static getPublicRecordsSlice(publicRecord, periodEnd, periodStart) {
    const filedDate = moment(publicRecord.filed_date);
    const dispositionDate = moment(publicRecord.disposition_date);

    if (dispositionDate.isBefore(periodStart) || filedDate.isAfter(periodEnd)) {
      return "";
    }
    if (dispositionDate.isAfter(periodStart) && filedDate.isBefore(periodEnd)) {
      const months = Math.round(
        moment.duration(dispositionDate.valueOf() - filedDate.valueOf()).asMonths(),
      );
      return "X".repeat(months);
    }
    if (dispositionDate.isAfter(periodStart)) {
      const months = Math.round(
        moment.duration(dispositionDate.valueOf() - periodStart.valueOf()).asMonths(),
      );
      return "X".repeat(months);
    }
    if (filedDate.isBefore(periodEnd)) {
      const months = Math.round(
        moment.duration(periodEnd.valueOf() - filedDate.valueOf()).asMonths(),
      );
      return "X".repeat(months);
    }
  }

  static getPaymentHistoryForMonth(tradeline, monthDate) {
    const paymentHistoryStartDate = moment(tradeline.payment_history_start_date);
    if (paymentHistoryStartDate.isSameOrAfter(monthDate, "months")) {
      const offset = Math.floor(
        moment.duration(paymentHistoryStartDate.valueOf() - monthDate.valueOf()).asMonths(),
      );
      return tradeline.payment_history.substring(offset, offset + 1);
    }
    return " ";
  }

  static getPublicRecordsForMonth(publicRecord, monthDate) {
    const filedDate = moment(publicRecord.filed_date);
    const dispositionDate = moment(publicRecord.disposition_date);
    if (
      dispositionDate.isSameOrAfter(monthDate, "months") &&
      filedDate.isSameOrBefore(monthDate, "months")
    ) {
      return "X";
    }
    return " ";
  }

  static getOwnershipStatus(borrowers: LoanApplicationBorrowers) {
    let ownershipStatus = "";
    Object.values(borrowers).forEach(b => {
      ownershipStatus = b.ownership_status;
    });
    if (!ownershipStatus) {
      return false;
    }
    const m = {
      own: "Primary in REO",
      rent: "Renter",
      livingrentfree: "Living Rent Free",
    };
    return m[ownershipStatus.toLowerCase()] || false;
  }

  renderPresentHousingExpenses() {
    const { app } = this.props;
    const { ownedRealEstate } = this.state;

    const borrowersAsStrings = getBorrowerEntitiesAsStrings(
      Object.values(app.borrowers).map(b => b),
    );
    const presentHousingExpenses = CreditSummaryTools.getPresentHousingExpenses(
      app.borrowers,
      ownedRealEstate,
    );

    let totalMonthlyHousingExpenses = 0;
    presentHousingExpenses.forEach(e => {
      e.associated_borrowers.forEach(b => {
        totalMonthlyHousingExpenses += Number(b.monthly_rent);
      });
      totalMonthlyHousingExpenses += Number(e.owned_real_estate?.total_monthly_pitia || 0);
    });

    return (
      <div className="credit_summary__table_container credit_summary__present_housing_expenses">
        <h2>Present Housing Expenses</h2>
        <Table className="credit_summary__present_housing_expenses__table">
          <thead>
            <tr>
              <th>Ownership Status</th>
              <th>Address</th>
              {borrowersAsStrings.map(b => (
                <th>{b} Monthly Rent</th>
              ))}
              <th>Monthly P&amp;I</th>
              <th>Annual Property Taxes</th>
              <th>Annual Hazard Insurance</th>
              <th>Monthly HOA / Condo Dues</th>
              <th>Monthly Other</th>
              <th>Total Monthly PITIA</th>
              <th>Associated Borrower(s)</th>
            </tr>
          </thead>
          <tbody>
            {presentHousingExpenses.length > 0 &&
              presentHousingExpenses.map(housing => {
                return (
                  // eslint-disable-next-line react/jsx-key
                  <tr>
                    <td>
                      {CreditSummaryHurdleEditor.getOwnershipStatus(housing.associated_borrowers)}
                    </td>
                    <td>{housing.address}</td>
                    {borrowersAsStrings.includes("Borrower 1") && (
                      <td>
                        <Money
                          value={
                            housing.associated_borrowers.find(
                              b => b.borrower_entity === BorrowerEntity.Borrower1,
                            )?.monthly_rent
                          }
                        />
                        <LearnMore text="Editable in the History Hurdle" />
                      </td>
                    )}
                    {borrowersAsStrings.includes("Borrower 2") && (
                      <td>
                        <Money
                          value={
                            housing.associated_borrowers.find(
                              b => b.borrower_entity === BorrowerEntity.Borrower2,
                            )?.monthly_rent
                          }
                        />
                        <LearnMore text="Editable in the History Hurdle" />
                      </td>
                    )}
                    {borrowersAsStrings.includes("Borrower 3") && (
                      <td>
                        <Money
                          value={
                            housing.associated_borrowers.find(
                              b => b.borrower_entity === BorrowerEntity.Borrower3,
                            )?.monthly_rent
                          }
                        />
                        <LearnMore text="Editable in the History Hurdle" />
                      </td>
                    )}
                    {borrowersAsStrings.includes("Borrower 4") && (
                      <td>
                        <Money
                          value={
                            housing.associated_borrowers.find(
                              b => b.borrower_entity === BorrowerEntity.Borrower4,
                            )?.monthly_rent
                          }
                        />
                        <LearnMore text="Editable in the History Hurdle" />
                      </td>
                    )}
                    <td>
                      <Money
                        value={
                          housing.owned_real_estate?.estimated_monthly_p_and_i ||
                          housing.owned_real_estate?.operator_monthly_p_and_i
                        }
                      />
                    </td>
                    <td>
                      <Money value={housing.owned_real_estate?.operator_annual_property_taxes} />
                    </td>
                    <td>
                      <Money
                        value={
                          housing.owned_real_estate?.estimated_monthly_hazard_insurance ||
                          housing.owned_real_estate?.operator_monthly_hazard_insurance
                        }
                      />
                    </td>
                    <td>
                      <Money
                        value={
                          housing.owned_real_estate?.estimated_monthly_hoa_or_condo_dues ||
                          housing.owned_real_estate?.operator_monthly_hoa_or_condo_dues
                        }
                      />
                    </td>
                    <td>
                      <Money value={housing.owned_real_estate?.operator_monthly_other} />
                    </td>
                    <td>
                      <Money value={housing.owned_real_estate?.total_monthly_pitia} />
                    </td>
                    <td>{getBorrowerEntitiesAsStrings(housing.associated_borrowers).join(", ")}</td>
                  </tr>
                );
              })}
          </tbody>
          <tfoot>
            <tr className="credit_summary__total_row">
              {/* colSpan is the total number of columns less 1. So 9 static columns, n - 1 borrower columns */}
              <td
                colSpan={9 + (borrowersAsStrings.length - 1)}
                className="credit_summary__total_row__label"
              >
                Total Monthly Present Housing Expenses:
              </td>
              <td>
                <Money value={totalMonthlyHousingExpenses} />
              </td>
              <td />
            </tr>
          </tfoot>
        </Table>
      </div>
    );
  }

  renderNetProceedsTdTag(reo) {
    if (reo.usable_net_sale_proceeds === null || !reo.proceeds_text) {
      return "—";
    }

    if (reo.proceeds_text.includes("Settlement Statement") || !reo.is_trade_in) {
      // hack: determine the doc type from the proceeds text message
      return [
        <Money value={reo.usable_net_sale_proceeds} />,
        <LearnMore text={reo.proceeds_text} />,
      ];
    }
    // For now, only allow editing of net proceeds if its a trade-in and if we have not ingested docs, via CC net equity val
    return [
      <EditableField
        inputType="money"
        inputName="cash_close_net_equity"
        fieldName="cash_close_net_equity"
        originalValue={reo.usable_net_sale_proceeds}
        changedValue={reo.cash_close_net_equity}
        handleOnChange={this.handleOwnedRealEstateChange.bind(this, reo.id)}
      />,
      <LearnMore text={reo.proceeds_text} />,
    ];
  }

  renderRealEstateOwned() {
    const { ownedRealEstate, deleteModalIsOpen } = this.state;
    return (
      <div className="credit_summary__table_container credit_summary__real_estate_owned">
        <h2>Real Estate Owned</h2>
        <NotificationsForTradeIn app={this.props.app} ownedRealEstate={ownedRealEstate} />
        <NotificationsForNetEquity
          appID={this.props.app.id}
          ownedRealEstate={ownedRealEstate}
          reloadApp={this.reloadApp.bind(this)}
        />
        <Table className="credit_summary__real_estate_owned__table">
          <thead>
            <tr>
              <th>Ownership Status</th>
              <th>Address</th>
              <th>Present Market Value</th>
              <th>Sale Price</th>
              <th>Amount of Mortgages &amp; Liens</th>
              <th>Monthly P&amp;I</th>
              <th>Annual Property Taxes</th>
              <th>Annual Hazard Insurance</th>
              <th>Monthly HOA / Condo Dues</th>
              <th>Monthly Other</th>
              <th>Total Monthly PITIA</th>
              <th>Associated Borrower(s)</th>
              <th>Associated Txns</th>
              <th>Usable Proceeds</th>
              <th>Trade-in</th>
              <th>Net Monthly Rental Income</th>
              <th>Docs</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {ownedRealEstate &&
              ownedRealEstate.map((realEstate, index) => {
                const associatedTradelineIDs = realEstate.tradeline_ids || [];
                const hoverClassName =
                  this.state.highlightedTradelines.length > 0 &&
                  associatedTradelineIDs.indexOf(this.state.highlightedTradelines[0]) >= 0
                    ? "credit_summary__row--hover"
                    : "credit_summary_row";
                return (
                  <tr
                    key={index}
                    className={hoverClassName}
                    onClick={this.handleClick.bind(this, associatedTradelineIDs)}
                  >
                    <Modal show={deleteModalIsOpen}>
                      <Modal.Header closeButton>
                        <Modal.Title>Mark this REO for deletion?</Modal.Title>
                      </Modal.Header>
                      <Modal.Body>
                        <div style={{ display: "flex", flexFlow: "column nowrap" }}>
                          <strong>
                            Remember to hit <em>Save</em> to save changes.
                          </strong>
                          <div style={{ marginBottom: "30px" }} />
                          <div>
                            <Button kind="secondary" onClick={this.closeDeleteModal.bind(this)}>
                              Cancel
                            </Button>
                            <Button
                              kind="danger"
                              onClick={this.deleteRealEstate.bind(
                                this,
                                this.state.realEstateMarkedForDeletion,
                              )}
                            >
                              Mark for deletion
                            </Button>
                          </div>
                        </div>
                      </Modal.Body>
                    </Modal>
                    <td>
                      <EditableField
                        inputType="select"
                        inputName={`operator_ownership_status_${index}`}
                        fieldName="operator_ownership_status"
                        originalValue={realEstate.ownership_status}
                        changedValue={realEstate.operator_ownership_status}
                        options={OWNERSHIP_OPTIONS}
                        handleOnChange={this.handleOwnedRealEstateChange.bind(this, realEstate.id)}
                      />
                    </td>
                    <td>
                      <EditableField
                        inputType="text"
                        inputName={`operator_address_line1_${index}`}
                        fieldName="operator_address_line1"
                        originalValue={realEstate.address_line1}
                        changedValue={realEstate.operator_address_line1 || undefined}
                        handleOnChange={this.handleOwnedRealEstateChange.bind(this, realEstate.id)}
                      />
                      <EditableField
                        inputType="text"
                        inputName={`operator_address_line2_${index}`}
                        fieldName="operator_address_line2"
                        originalValue={realEstate.address_line2}
                        changedValue={realEstate.operator_address_line2 || undefined}
                        handleOnChange={this.handleOwnedRealEstateChange.bind(this, realEstate.id)}
                      />
                      <br />
                      <EditableField
                        inputType="text"
                        inputName={`operator_city_${index}`}
                        fieldName="operator_city"
                        originalValue={realEstate.city}
                        changedValue={realEstate.operator_city || undefined}
                        handleOnChange={this.handleOwnedRealEstateChange.bind(this, realEstate.id)}
                      />
                      <EditableField
                        inputType="text"
                        inputName={`operator_state_${index}`}
                        fieldName="operator_state"
                        originalValue={realEstate.state}
                        changedValue={realEstate.operator_state || undefined}
                        handleOnChange={this.handleOwnedRealEstateChange.bind(this, realEstate.id)}
                      />
                      <EditableField
                        inputType="text"
                        inputName={`operator_postal_code_${index}`}
                        fieldName="operator_postal_code"
                        originalValue={realEstate.postal_code}
                        changedValue={realEstate.operator_postal_code || undefined}
                        handleOnChange={this.handleOwnedRealEstateChange.bind(this, realEstate.id)}
                      />
                    </td>
                    <td>
                      <EditableField
                        inputType="money"
                        inputName={`home_value_${index}`}
                        fieldName="home_value"
                        originalValue={realEstate.home_value}
                        changedValue={realEstate.home_value}
                        handleOnChange={this.handleOwnedRealEstateChange.bind(this, realEstate.id)}
                      />
                    </td>
                    <td>
                      <Money value={realEstate.effective_home_value} />
                    </td>
                    <td>
                      <Money value={realEstate.total_remaining_mortgage_balance} />
                    </td>
                    <td>
                      <EditableField
                        inputType="money"
                        inputName={`operator_monthly_p_and_i_${index}`}
                        fieldName="operator_monthly_p_and_i"
                        originalValue={realEstate.estimated_monthly_p_and_i || 0}
                        changedValue={realEstate.operator_monthly_p_and_i}
                        handleOnChange={this.handleOwnedRealEstateChange.bind(this, realEstate.id)}
                      />
                      <a onClick={this.linkTradelinesToRealEstate.bind(this, realEstate.id)}>
                        Link Tradelines
                      </a>
                    </td>
                    <td>
                      <EditableField
                        inputType="selectBool"
                        inputName={`operator_annual_property_taxes_escrowed_${index}`}
                        fieldName="operator_annual_property_taxes_escrowed"
                        originalValue={realEstate.monthly_property_taxes_escrowed}
                        changedValue={realEstate.operator_annual_property_taxes_escrowed}
                        options={ESCROWED_OPTIONS}
                        handleOnChange={this.handleOwnedRealEstateChange.bind(this, realEstate.id)}
                      />
                      {(realEstate.operator_annual_property_taxes_escrowed === false ||
                        (realEstate.operator_annual_property_taxes_escrowed !== true &&
                          realEstate.monthly_property_taxes_escrowed === false)) && (
                        <EditableField
                          inputType="money"
                          inputName={`operator_annual_property_taxes_${index}`}
                          fieldName="operator_annual_property_taxes"
                          originalValue={realEstate.monthly_property_taxes}
                          changedValue={realEstate.operator_annual_property_taxes}
                          handleOnChange={this.handleOwnedRealEstateChange.bind(
                            this,
                            realEstate.id,
                          )}
                        />
                      )}
                    </td>
                    <td>
                      <EditableField
                        inputType="selectBool"
                        inputName={`operator_annual_hazard_insurance_escrowed_${index}`}
                        fieldName="operator_annual_hazard_insurance_escrowed"
                        originalValue={realEstate.annual_hazard_insurance_escrowed}
                        changedValue={realEstate.operator_annual_hazard_insurance_escrowed}
                        options={ESCROWED_OPTIONS}
                        handleOnChange={this.handleOwnedRealEstateChange.bind(this, realEstate.id)}
                      />
                      {(realEstate.operator_annual_hazard_insurance_escrowed === false ||
                        (realEstate.operator_annual_hazard_insurance_escrowed !== true &&
                          realEstate.annual_hazard_insurance_escrowed === false)) && (
                        <EditableField
                          inputType="money"
                          inputName={`operator_annual_hazard_insurance_${index}`}
                          fieldName="operator_annual_hazard_insurance"
                          originalValue={realEstate.annual_hazard_insurance}
                          changedValue={realEstate.operator_annual_hazard_insurance}
                          handleOnChange={this.handleOwnedRealEstateChange.bind(
                            this,
                            realEstate.id,
                          )}
                        />
                      )}
                    </td>
                    <td>
                      <EditableField
                        inputType="money"
                        inputName={`operator_monthly_hoa_or_condo_dues_${index}`}
                        fieldName="operator_monthly_hoa_or_condo_dues"
                        originalValue={
                          realEstate.monthly_hoa_or_condo_dues ||
                          realEstate.estimated_monthly_hoa_or_condo_dues
                        }
                        changedValue={realEstate.operator_monthly_hoa_or_condo_dues}
                        handleOnChange={this.handleOwnedRealEstateChange.bind(this, realEstate.id)}
                      />
                    </td>
                    <td>
                      <EditableField
                        inputType="money"
                        inputName={`operator_monthly_other_${index}`}
                        fieldName="operator_monthly_other"
                        originalValue={0}
                        changedValue={realEstate.operator_monthly_other}
                        handleOnChange={this.handleOwnedRealEstateChange.bind(this, realEstate.id)}
                      />
                    </td>
                    <td>
                      <Money value={realEstate.total_monthly_pitia} />
                    </td>
                    <td>
                      <EditableField
                        inputType="select"
                        inputName={`borrower_entity_${index}`}
                        fieldName="borrower_entity"
                        originalValue={realEstate.borrower_entity}
                        changedValue={realEstate.borrower_entity}
                        options={BORROWER_ENTITY_OPTIONS}
                        handleOnChange={this.handleOwnedRealEstateChange.bind(this, realEstate.id)}
                      />
                    </td>
                    <td>
                      <a onClick={this.linkLoEsToRealEstate.bind(this, realEstate.id)}>txns</a>
                    </td>
                    <td>{this.renderNetProceedsTdTag(realEstate)}</td>
                    <td>
                      <EditableField
                        inputType="selectBool"
                        inputName={`is_trade_in_${index}`}
                        fieldName="is_trade_in"
                        originalValue={realEstate.is_trade_in}
                        changedValue={realEstate.is_trade_in}
                        options={YES_NO_OPTIONS}
                        handleOnChange={this.handleOwnedRealEstateChange.bind(this, realEstate.id)}
                      />
                    </td>
                    <td>
                      {realEstate.net_monthly_rental_income ? (
                        <Money value={realEstate.net_monthly_rental_income} />
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      {realEstate.documents && realEstate.documents.length > 0 && (
                        <ul>
                          {realEstate.documents.map((doc, i) => (
                            <li key={i}>
                              <a onClick={this.toggleViewDocument.bind(this, doc.id)}>
                                {doc.display_text || humanize(doc.type)} [ID: {doc.id}]
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                      {this.state.viewingDocumentID && (
                        <CarbonModal
                          size="lg"
                          passiveModal
                          open={this.state.viewingDocumentID !== null}
                          onRequestClose={() => this.setState({ viewingDocumentID: null })}
                        >
                          <DocEntry
                            app={this.props.app}
                            docId={this.state.viewingDocumentID}
                            hurdle={this.props.hurdle}
                            docNotFoundMsg=""
                            modalTitle="Review Lease"
                            onHide={this.toggleViewDocument.bind(
                              this,
                              this.state.viewingDocumentID,
                            )}
                          />
                        </CarbonModal>
                      )}
                    </td>
                    <td>
                      <Button
                        kind="ghost"
                        onClick={this.openDeleteModal.bind(this, realEstate.id)}
                        renderIcon={TrashCan16}
                        iconDescription="Delete Real Estate record"
                        tooltipPosition="left"
                        danger
                        hasIconOnly
                      />
                    </td>
                  </tr>
                );
              })}
            <tr>
              <td colSpan={16}>
                <Button
                  onClick={this.addNewRealEstate.bind(this)}
                  kind="tertiary"
                  size="small"
                  renderIcon={Add16}
                >
                  Add Real Estate
                </Button>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr className="credit_summary__total_row">
              <td colSpan={10} className="credit_summary__total_row__label">
                Total Monthly Housing Expenses:
              </td>
              <td colSpan={1}>
                <Money
                  value={ownedRealEstate.reduce((acc, realEstate) => {
                    if (
                      (realEstate.property_type === "Investment property" &&
                        !realEstate.operator_ownership_status) ||
                      realEstate.operator_ownership_status === "RentedOut"
                    ) {
                      return acc;
                    }
                    if (
                      realEstate.operator_ownership_status === "Pending Sale" ||
                      realEstate.operator_ownership_status === "Sold"
                    ) {
                      return acc;
                    }
                    return acc + Number(realEstate.total_monthly_pitia);
                  }, 0)}
                />
              </td>
              <td colSpan={5} />
            </tr>
          </tfoot>
        </Table>
      </div>
    );
  }

  toggleViewDocument(documentID) {
    if (this.state.viewingDocumentID === documentID) {
      this.setState({ viewingDocumentID: null });
    } else {
      this.setState({ viewingDocumentID: documentID });
    }
  }

  addNewRealEstate() {
    const newState = this.state;
    const newRealEstate = {
      id: uuid.v4(),
      loan_application_id: this.props.app.id,
    };
    newState.ownedRealEstate.push(newRealEstate);
    this.setState(newState);
  }

  renderAdditionalOpenLiabilities() {
    const { additionalLiabilities } = this.state;

    let totalMonthlyAdditionalLiabilities = 0;
    let totalMonthlyAdditionalLiabilitiesExcludedFromDTI = 0;
    return (
      <div className="credit_summary__table_container credit_summary__additional_open_liabilities">
        <h2>Additional Liabilities</h2>
        <a onClick={this.toggleDebtLoEs.bind(this)}>Show debt explanations</a>
        {this.state.showDebtExplanations && this.renderDebtLoEs()}
        <Table className="credit_summary__additional_open_liabilities__table">
          <thead>
            <tr>
              <th>Include in Ratios</th>
              <th>Creditor Name</th>
              <th>Acct #</th>
              <th>Acct Type</th>
              <th>Category</th>
              <th>Monthly Payment</th>
              <th>Balance</th>
              <th>Remaining Payments</th>
              <th>Associated Borrower(s)</th>
              <th>Associated Txns</th>
              <th>Docs</th>
              <th>Note</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {additionalLiabilities &&
              additionalLiabilities.map((liability, index) => {
                const originalLiabilityIndex = _.findIndex(this.props.app.additional_debts, {
                  id: liability.id,
                });
                const originalLiability: any =
                  originalLiabilityIndex >= 0
                    ? this.props.app.additional_debts[originalLiabilityIndex]
                    : null;

                const monthlyPaymentAmount = Number(liability.monthly_payment_amount);
                const remainingPayments = Math.ceil(
                  liability.current_balance_amount / monthlyPaymentAmount,
                );
                const includeInRatios = !(
                  liability.operator_include_in_ratios === false ||
                  (liability.operator_include_in_ratios !== true &&
                    liability.include_in_ratios === false)
                );
                const rowClassName = includeInRatios
                  ? "credit_summary__row"
                  : "credit_summary__row--faded";
                if (!includeInRatios) {
                  totalMonthlyAdditionalLiabilitiesExcludedFromDTI += monthlyPaymentAmount;
                } else {
                  totalMonthlyAdditionalLiabilities += monthlyPaymentAmount;
                }
                const hoverClassName =
                  this.state.highlightedTradelines.indexOf(liability.id) >= 0
                    ? "credit_summary__row--hover"
                    : "credit_summary_row";
                return (
                  <tr
                    key={index}
                    className={`${rowClassName} ${hoverClassName}`}
                    onClick={this.handleClick.bind(this, [liability.id])}
                  >
                    <td>
                      <EditableField
                        inputType="selectBool"
                        inputName={`operator_include_in_ratios_${index}`}
                        fieldName="operator_include_in_ratios"
                        originalValue={liability.include_in_ratios}
                        changedValue={liability.operator_include_in_ratios}
                        options={YES_NO_OPTIONS}
                        handleOnChange={this.handleLiabilityChange.bind(this, liability.id)}
                      />
                      {liability.include_in_ratios_reason && (
                        <LearnMore placement="right" text={liability.include_in_ratios_reason} />
                      )}
                      {liability.operator_include_in_ratios === false && (
                        <EditableField
                          inputType="select"
                          inputName={`operator_not_included_reason_${index}`}
                          fieldName="operator_not_included_reason"
                          originalValue=""
                          changedValue={liability.operator_not_included_reason}
                          options={NOT_INCLUDED_OPTIONS}
                          handleOnChange={this.handleLiabilityChange.bind(this, liability.id)}
                          editing={!liability.operator_not_included_reason}
                          placeholder="Please select a reason"
                        />
                      )}
                    </td>
                    <td>
                      <EditableField
                        inputType="text"
                        inputName={`creditor_name_${index}`}
                        fieldName="creditor_name"
                        originalValue={
                          (originalLiability && originalLiability.creditor_name) ||
                          liability.creditor_name
                        }
                        changedValue={liability.creditor_name}
                        handleOnChange={this.handleLiabilityChange.bind(this, liability.id)}
                      />
                    </td>
                    <td>
                      <EditableField
                        inputType="text"
                        inputName={`account_number_${index}`}
                        fieldName="account_number"
                        originalValue={
                          (originalLiability && originalLiability.account_number) ||
                          liability.account_number
                        }
                        changedValue={liability.account_number}
                        handleOnChange={this.handleLiabilityChange.bind(this, liability.id)}
                      />
                    </td>
                    <td>
                      <EditableField
                        inputType="select"
                        inputName={`account_type_${index}`}
                        fieldName="account_type"
                        originalValue={
                          (originalLiability && originalLiability.account_type) ||
                          liability.account_type
                        }
                        changedValue={liability.account_type}
                        options={TRADELINE_ACCOUNT_TYPE_OPTIONS}
                        handleOnChange={this.handleLiabilityChange.bind(this, liability.id)}
                      />
                    </td>
                    <td>
                      <EditableField
                        inputType="select"
                        inputName={`credit_loan_type_${index}`}
                        fieldName="credit_loan_type"
                        originalValue={
                          (originalLiability && originalLiability.credit_loan_type) ||
                          liability.credit_loan_type
                        }
                        changedValue={liability.credit_loan_type}
                        options={CATEGORY_OPTIONS}
                        handleOnChange={this.handleLiabilityChange.bind(this, liability.id)}
                      />
                    </td>
                    <td>
                      <EditableField
                        inputType="money"
                        inputName={`monthly_payment_amount_${index}`}
                        fieldName="monthly_payment_amount"
                        originalValue={
                          (originalLiability && originalLiability.monthly_payment_amount) ||
                          liability.monthly_payment_amount ||
                          0
                        }
                        changedValue={liability.monthly_payment_amount}
                        handleOnChange={this.handleLiabilityChange.bind(this, liability.id)}
                      />
                    </td>
                    <td>
                      <EditableField
                        fieldName="current_balance_amount"
                        inputName={`current_balance_amount_${index}`}
                        inputType="money"
                        originalValue={
                          (originalLiability && originalLiability.current_balance_amount) ||
                          liability.current_balance_amount ||
                          0
                        }
                        changedValue={liability.current_balance_amount}
                        handleOnChange={this.handleLiabilityChange.bind(this, liability.id)}
                      />
                    </td>
                    <td>{isNaN(remainingPayments) ? 0 : remainingPayments}</td>
                    <td>
                      <EditableField
                        inputType="select"
                        inputName={`borrower_entity_${index}`}
                        fieldName="borrower_entity"
                        originalValue={
                          (originalLiability && originalLiability.borrower_entity) ||
                          liability.borrower_entity
                        }
                        changedValue={liability.borrower_entity}
                        options={BORROWER_ENTITY_OPTIONS}
                        handleOnChange={this.handleLiabilityChange.bind(this, liability.id)}
                      />
                    </td>
                    <td>TBD</td>
                    <td>-</td>
                    <td className="credit_summary__note_column">
                      <EditableField
                        fieldName="description"
                        inputName={`description_${index}`}
                        inputType="textarea"
                        originalValue={
                          (originalLiability && originalLiability.description) ||
                          liability.description ||
                          undefined
                        }
                        changedValue={liability.description || undefined}
                        handleOnChange={this.handleLiabilityChange.bind(this, liability.id)}
                      />
                    </td>
                    <td>
                      <Button
                        onClick={this.deleteLiability.bind(this, liability.id)}
                        renderIcon={TrashCan16}
                        hasIconOnly
                      />
                    </td>
                  </tr>
                );
              })}
            <tr>
              <td colSpan={11}>
                <Button
                  onClick={this.addNewLiability.bind(this)}
                  kind="tertiary"
                  size="small"
                  renderIcon={Add16}
                >
                  Add Liability
                </Button>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr className="credit_summary__total_row">
              <td colSpan={3} className="credit_summary__total_row__label">
                Total monthly liabilities from other sources:
              </td>
              <td>
                <Money value={totalMonthlyAdditionalLiabilities} />
              </td>
              <td colSpan={3} className="credit_summary__total_row__label">
                Total monthly liabilities excluded from DTI:
              </td>
              <td>
                <Money value={totalMonthlyAdditionalLiabilitiesExcludedFromDTI} />
              </td>
              <td colSpan={7} />
            </tr>
          </tfoot>
        </Table>
      </div>
    );
  }

  addNewLiability() {
    const newState = this.state;
    const newLiability = {
      id: uuid.v4(),
      loan_application_id: this.props.app.id,
    };
    newState.additionalLiabilities.push(newLiability);
    this.setState(newState);
  }

  renderTradelines() {
    /**
     * Sort tradelines with derogatory information first.
     * And then the rest can be in reverse chronological order within Open Accounts,
     * Paid Accounts and then Closed Accounts
     */
    const { tradelines } = this.state;
    const publicRecords = _.flatten(this.props.app.credit_reports.map(cr => cr.public_records));

    const startingDate = moment(this.props.app.created_at);
    const months = [];
    for (let i = 0; i < 60; i += 1) {
      const oldDate = startingDate.clone();
      months.push(oldDate);
      startingDate.subtract(1, "months");
    }
    const manyYearsBefore = moment(this.props.app.created_at).subtract(100, "years");

    return (
      <div className="credit_summary__tradelines credit_summary__table_container">
        <h2>Tradelines</h2>
        <Table className="credit_summary__tradelines__table">
          <thead>
            <tr>
              <th>CreditorName</th>
              <th>Status</th>
              <th>Type</th>
              <th>Mon Pmt</th>
              <th>Balance</th>
              <th>Dispute?</th>
              <th>Med?</th>
              <th>Whose?</th>
              {months.map((month, i) => {
                if (i % 2 === 0) {
                  return (
                    <th
                      className={`credit_summary__column--skinny \
credit_summary__column_header--rotated`}
                      key={i}
                    >
                      {month.format("MMM YYYY")}
                    </th>
                  );
                }
                return (
                  <th
                    className={`credit_summary__column--skinny \
credit_summary__column_header--rotated`}
                    key={i}
                  />
                );
              })}
              <th className="credit_summary__column--header--history">Remaining History</th>
              <th className="credit_summary__column--header--note">Note</th>
            </tr>
          </thead>
          <tbody>
            {publicRecords &&
              publicRecords.map((publicRecord, index) => (
                <tr key={index}>
                  <td>{publicRecord.court_name}</td>
                  <td>{publicRecord.disposition_type}</td>
                  <td>{publicRecord.type}</td>
                  <td />
                  <td>
                    <Money value={Number(publicRecord.obligation_amount)} />
                  </td>
                  <td>{publicRecord.consumer_dispute_flag ? "Yes" : "No"}</td>
                  <td />
                  <td>
                    {CreditSummaryHurdleEditor.getAssociatedBorrowers(publicRecord.ownership_type)}
                  </td>
                  {months.map((month, i) => (
                    <td className="credit_summary__column--skinny" key={i}>
                      {CreditSummaryHurdleEditor.getPublicRecordsForMonth(publicRecord, month)}
                    </td>
                  ))}
                  <td>
                    {CreditSummaryHurdleEditor.getPublicRecordsSlice(
                      publicRecord,
                      months[months.length - 1],
                      manyYearsBefore,
                    )}
                  </td>
                  <td />
                </tr>
              ))}
            {tradelines &&
              tradelines.map((tradeline, index) => {
                const hoverClassName =
                  this.state.highlightedTradelines.indexOf(tradeline.id) >= 0
                    ? "credit_summary__row--hover"
                    : "credit_summary_row";
                return (
                  <tr
                    key={index}
                    className={hoverClassName}
                    onClick={this.handleClick.bind(this, [tradeline.id])}
                  >
                    <td>{tradeline.creditor_name}</td>
                    <td>{tradeline.account_status}</td>
                    <td>{tradeline.account_type}</td>
                    <td>
                      <Money value={tradeline.monthly_payment_amount} />
                    </td>
                    <td>
                      <Money value={tradeline.current_balance_amount} />
                    </td>
                    <td>
                      <EditableField
                        inputType="selectBool"
                        inputName={`operator_consumer_dispute_flag_${index}`}
                        fieldName="operator_consumer_dispute_flag"
                        originalValue={tradeline.consumer_dispute_flag}
                        changedValue={tradeline.operator_consumer_dispute_flag}
                        options={YES_NO_OPTIONS}
                        handleOnChange={this.handleTradelineChange.bind(this, tradeline.id)}
                      />
                    </td>
                    <td>
                      <EditableField
                        inputType="selectBool"
                        inputName={`operator_is_medical_flag_${index}`}
                        fieldName="operator_is_medical_flag"
                        originalValue={tradeline.is_medical}
                        changedValue={tradeline.operator_is_medical_flag}
                        options={YES_NO_OPTIONS}
                        handleOnChange={this.handleTradelineChange.bind(this, tradeline.id)}
                      />
                    </td>
                    <td>
                      {renderBorrowerEntity(tradeline.borrower_entity, this.props.app.borrowers)}
                    </td>
                    {months.map((month, i) => (
                      <td className="credit_summary__column--skinny" key={i}>
                        {CreditSummaryHurdleEditor.getPaymentHistoryForMonth(tradeline, month)}
                      </td>
                    ))}
                    <td className="credit_summary__history_column">
                      {CreditSummaryHurdleEditor.getPaymentHistorySlice(
                        tradeline,
                        months[months.length - 1],
                        manyYearsBefore,
                      )}
                    </td>
                    <td className="credit_summary__note_column">
                      <EditableField
                        fieldName="operator_note"
                        inputName={`operator_note_${index}`}
                        inputType="textarea"
                        originalValue=""
                        changedValue={tradeline.operator_note ? tradeline.operator_note : undefined}
                        handleOnChange={this.handleTradelineChange.bind(this, tradeline.id)}
                      />
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </Table>
      </div>
    );
  }

  static extractResponse(explanation) {
    // no upload explanation types in the set we show here, so pass empty docs
    return ExplanationsHurdleEditor.extractResponse(explanation, []);
  }

  transactionsForTradeline(tradeline) {
    const { explanations } = this.state;
    let transactions = [];
    for (let i = 0; i < explanations.length; i += 1) {
      const explanation = explanations[i];
      if (tradeline.explanation_ids && tradeline.explanation_ids.indexOf(explanation.id) >= 0) {
        transactions = transactions.concat(explanation.transactions);
      }
    }
    return transactions;
  }

  renderLoELinker() {
    const { explanations } = this.state;
    const tradeline =
      this.state.tradelines[this.findIndexOfTradelineWithID(this.state.showingLoETradelineLinker)];
    const transactions = this.transactionsForTradeline(tradeline);

    return (
      <Modal
        className="credit_summary__loe_linker"
        show
        onHide={this.hideLoETradelineLinker.bind(this)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Link Explanations</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <p>Please select the LOEs associated with this tradeline:</p>
            <Panel>
              <Panel.Heading>Tradeline</Panel.Heading>
              <Panel.Body>
                <dl className="dl-horizontal">
                  <dt>Creditor Name</dt>
                  <dd>{tradeline.creditor_name}</dd>
                  <dt>Account Type</dt>
                  <dd>{tradeline.account_type}</dd>
                  <dt>Monthly Payment</dt>
                  <dd>{tradeline.calculated_monthly_payment}</dd>
                  <dt>Current Balance</dt>
                  <dd>{tradeline.current_balance_amount}</dd>
                </dl>
                <Table
                  striped
                  bordered
                  condensed
                  className="credit_summary__loe_linker_transaction_table"
                >
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions &&
                      transactions.map((transaction, index) => (
                        <tr key={index}>
                          <td>
                            <FormattedDate value={transaction.date} timeZone="UTC" />
                          </td>
                          <td>{transaction.memo}</td>
                          <td>
                            <Money value={transaction.value} />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </Panel.Body>
            </Panel>
            <Table striped bordered condensed className="credit_summary__loe_linker_table">
              <thead>
                <tr>
                  <th>Explanation message</th>
                  <th>Borrower response</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {explanations &&
                  explanations.map((explanation, index) => [
                    <tr key={index * 2}>
                      <td>{explanation.explanation_type.display_text}</td>
                      <td>{CreditSummaryHurdleEditor.extractResponse(explanation)}</td>
                      <td>
                        <input
                          type="checkbox"
                          value={explanation.id}
                          onChange={this.handleLoELink.bind(
                            this,
                            this.state.showingLoETradelineLinker,
                            explanation.id,
                          )}
                          checked={tradeline.explanation_ids.indexOf(explanation.id) >= 0}
                        />
                      </td>
                    </tr>,
                    <tr key={index * 2 + 1}>
                      <td colSpan={3}>
                        <ExplanationTransactions app={this.props.app} explanation={explanation} />
                      </td>
                    </tr>,
                  ])}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  renderLoERealEstateLinker() {
    const { explanations } = this.state;
    const realEstate =
      this.state.ownedRealEstate[
        this.findIndexOfOwnedRealEstateWithID(this.state.showingLoERealEstateLinker)
      ];
    return (
      <Modal
        className="credit_summary__tradeline_linker"
        show
        onHide={this.hideLoERealEstateLinker.bind(this)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Link Explanations &amp; Real Estate</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <p>Please select the Explanations associated with this residence:</p>
            <Table striped bordered condensed className="credit_summary__loe_linker_table">
              <thead>
                <tr>
                  <th>Explanation message</th>
                  <th>Borrower response</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {explanations &&
                  explanations.map((explanation, index) => [
                    <tr key={index * 2}>
                      <td>{explanation.explanation_type.display_text}</td>
                      <td>{CreditSummaryHurdleEditor.extractResponse(explanation)}</td>
                      <td>
                        <input
                          type="checkbox"
                          value={explanation.id}
                          onChange={this.handleLoERealEstateLink.bind(
                            this,
                            this.state.showingLoERealEstateLinker,
                            explanation.id,
                          )}
                          checked={realEstate.explanation_ids.indexOf(explanation.id) >= 0}
                        />
                      </td>
                    </tr>,
                    <tr key={index * 2 + 1}>
                      <td colSpan={3}>
                        <ExplanationTransactions app={this.props.app} explanation={explanation} />
                      </td>
                    </tr>,
                  ])}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  renderTradelineLinker() {
    const { tradelines, additionalLiabilities, showingTradelineLinker } = this.state;
    let property = null;
    if (typeof showingTradelineLinker !== "string") {
      const index = this.findIndexOfOwnedRealEstateWithID(this.state.showingTradelineLinker);
      property = this.state.ownedRealEstate[index];
    }
    return (
      <Modal
        className="credit_summary__tradeline_linker"
        show
        onHide={this.hideTradelineLinker.bind(this)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Link Tradelines &amp; Real Estate</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {typeof this.state.showingTradelineLinker === "string" && (
            <div>
              Please hit "save" to sync the residence to the backend before you can link it with
              mortgage tradelines.
            </div>
          )}
          {typeof this.state.showingTradelineLinker !== "string" && (
            <div>
              <p>Please select the Tradelines associated with this residence:</p>
              <Table className="credit_summary__tradeline_linker_table">
                <thead>
                  <tr>
                    <th />
                    <th>Creditor Name</th>
                    <th>Account Type</th>
                    <th>Credit Loan Type</th>
                    <th>Monthly Payment</th>
                    <th>Current Balance</th>
                    <th>Limit</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(property.monthly_p_and_i !== null ||
                    property.other_mortgage_balance !== null) && (
                    <tr>
                      <td>
                        <input
                          type="checkbox"
                          onChange={ev => {
                            this.handleOwnedRealEstateChange(property.id, {
                              name: "exclude_borrower_manual_values",
                              value: !ev.target.checked,
                            });
                          }}
                          checked={!property.exclude_borrower_manual_values}
                        />
                      </td>
                      <td>
                        {property.monthly_p_and_i !== "0"
                          ? "A different mortgage"
                          : "Owned outright"}
                      </td>
                      <td />
                      <td />
                      <td>
                        <Money value={property.monthly_p_and_i} />
                      </td>
                      <td>
                        <Money value={property.other_mortgage_balance} />
                      </td>
                      <td />
                      <td />
                    </tr>
                  )}
                  {additionalLiabilities &&
                    additionalLiabilities.map((liability, index) => {
                      if (
                        liability.account_type !== "Mortgage" &&
                        liability.account_type !== "HELOC"
                      ) {
                        return null;
                      }
                      return (
                        <tr>
                          <td>
                            <input
                              type="checkbox"
                              value={liability.id}
                              onChange={this.handleAdditionalLiabilityLink.bind(
                                this,
                                this.state.showingTradelineLinker,
                                liability.id,
                              )}
                              checked={
                                liability.owned_real_estate_id === this.state.showingTradelineLinker
                              }
                            />
                          </td>
                          <td>{liability.creditor_name}</td>
                          <td>{liability.account_type}</td>
                          <td>{liability.credit_loan_type}</td>
                          <td>
                            <Money value={liability.monthly_payment_amount} />
                          </td>
                          <td>
                            <Money value={liability.current_balance_amount} />
                          </td>
                          <td>n/a</td>
                          <td>
                            <EditableField
                              inputType="select"
                              inputName={`operator_not_included_reason_${index}`}
                              fieldName="operator_not_included_reason"
                              originalValue="In Ratios"
                              changedValue={liability.operator_not_included_reason}
                              options={NOT_INCLUDED_OPTIONS}
                              handleOnChange={this.handleOwnedRealEstateLiabilityChange.bind(
                                this,
                                liability.id,
                              )}
                              editing={false}
                              placeholder="Please select a status"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  {tradelines &&
                    tradelines.map((tradeline, index) => {
                      if (
                        !tradeline.is_real_estate ||
                        (tradeline.owned_real_estate_id &&
                          tradeline.owned_real_estate_id !== this.state.showingTradelineLinker)
                      ) {
                        return null;
                      }
                      return (
                        <tr key={index}>
                          <td>
                            <input
                              type="checkbox"
                              value={tradeline.id}
                              onChange={this.handleTradelineLink.bind(
                                this,
                                this.state.showingTradelineLinker,
                                tradeline.id,
                              )}
                              checked={
                                tradeline.owned_real_estate_id === this.state.showingTradelineLinker
                              }
                            />
                          </td>
                          <td>{tradeline.creditor_name}</td>
                          <td>
                            {tradeline.operator_account_type !== null
                              ? tradeline.operator_account_type
                              : tradeline.account_type}
                          </td>
                          <td>
                            {tradeline.account_type === "Mortgage" && (
                              <EditableField
                                inputType="select"
                                inputName={`operator_category_${index}`}
                                fieldName="operator_category"
                                originalValue={tradeline.credit_loan_type}
                                changedValue={tradeline.operator_category}
                                options={MORTGAGE_TYPE_OPTIONS}
                                handleOnChange={this.handleOwnedRealEstateTradelineChange.bind(
                                  this,
                                  tradeline.id,
                                )}
                                editing={false}
                                placeholder="Please select a Mortgage Type"
                              />
                            )}
                            {tradeline.account_type !== "Mortgage" && tradeline.credit_loan_type}
                          </td>
                          <td>
                            <Money value={tradeline.monthly_payment_amount} />
                          </td>
                          <td>
                            <Money value={tradeline.current_balance_amount} />
                          </td>
                          <td>
                            <Money value={tradeline.account_limit} />
                          </td>
                          <td>
                            <EditableField
                              inputType="select"
                              inputName={`operator_not_included_reason_${index}`}
                              fieldName="operator_not_included_reason"
                              originalValue="In Ratios"
                              changedValue={tradeline.operator_not_included_reason}
                              options={NOT_INCLUDED_OPTIONS}
                              handleOnChange={this.handleOwnedRealEstateTradelineChange.bind(
                                this,
                                tradeline.id,
                              )}
                              editing={false}
                              placeholder="Please select a status"
                            />
                            {tradeline.operator_not_included_reason === "To Be Paid" && (
                              <dl className="dl-horizontal">
                                <dt>Payoff Amount:</dt>
                                <dd>
                                  <EditableField
                                    fieldName="operator_balance"
                                    inputName={`operator_balance_${index}`}
                                    inputType="money"
                                    originalValue={tradeline.current_balance_amount}
                                    changedValue={tradeline.operator_balance}
                                    handleOnChange={this.handleTradelineChange.bind(
                                      this,
                                      tradeline.id,
                                    )}
                                  />
                                </dd>
                                <dt>Good Thru:</dt>
                                <dt>Last Payment:</dt>
                                <dt>Per Diem Interest:</dt>
                              </dl>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={this.hideTradelineLinker.bind(this)} renderIcon={Link16}>
            Link tradelines
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  createLOELiability(explanation) {
    const monthlyPayment = explanation.transactions[0].value;
    let allAmountsEqual = true;
    for (let i = 1; i < explanation.transactions.length; i += 1) {
      if (explanation.transactions[i].value !== monthlyPayment) {
        allAmountsEqual = false;
        break;
      }
    }

    const creditorNameGuess = explanation.transactions[0].memo.substr(0, 25);

    const newState = this.state;
    const newLiability: any = {
      id: uuid.v4(),
      loan_application_id: this.props.app.id,
      creditor_name: creditorNameGuess,
    };
    if (allAmountsEqual) {
      newLiability.monthly_payment_amount = Math.abs(monthlyPayment);
    }
    newState.additionalLiabilities.push(newLiability);
    newState.debtExplanationIDsWithNewLiabilities.push(explanation.id);
    this.setState(newState);
  }

  renderDebtLoEs() {
    const { explanations } = this.state;
    const debtExplanations = _.filter(
      explanations,
      (explanation: any) =>
        explanation.type_key === "PossibleDebtObligations" ||
        explanation.type_key === "PossibleRecurringDebtObligations",
    );
    return (
      <Modal className="credit_summary__debt_loes" show onHide={this.toggleDebtLoEs.bind(this)}>
        <Modal.Header closeButton>
          <Modal.Title>Debt Explanations</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <Table striped bordered condensed className="credit_summary__loe_linker_table">
              <thead>
                <tr>
                  <th>Explanation message</th>
                  <th>Borrower response</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {debtExplanations &&
                  debtExplanations.map((explanation, index) => {
                    const alreadyAdded = this.state.debtExplanationIDsWithNewLiabilities.includes(
                      explanation.id,
                    );
                    return [
                      <tr key={index * 2}>
                        <td>{explanation.explanation_type.display_text}</td>
                        <td>{CreditSummaryHurdleEditor.extractResponse(explanation)}</td>
                        <td>
                          {!alreadyAdded && (
                            <a onClick={this.createLOELiability.bind(this, explanation)}>Add</a>
                          )}
                        </td>
                      </tr>,
                      <tr key={index * 2 + 1}>
                        <td colSpan={3}>
                          <ExplanationTransactions app={this.props.app} explanation={explanation} />
                        </td>
                      </tr>,
                    ];
                  })}
                {!debtExplanations && (
                  <tr>
                    <td colSpan={3}>No debt explanations</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  // TODO this logic should be moved to the uw engine
  inquiryCheckNotRequiredReason() {
    const { inquiries } = this.state;
    const { app } = this.props;

    // TODO may need to dedupe same inquiries from TUC, XPN, EFX
    if (parseFloat(app.decision.dti) < 0.35) {
      return `DTI (${app.decision.dti})is less than 0.35`;
    }
    if (
      parseFloat(app.decision.available_funds) / parseFloat(app.decision.downpayment_estimate) >
      1.5
    ) {
      return `Available funds (${app.decision.available_funds}) are greater than 150% of estimated \
downpayment amount (${app.decision.downpayment_estimate})`;
    }
    let uniqueInquiryCounter = 0;
    let unmatchedAutoInquiry = false;
    let unmatchedRealEstateInquiry = false;
    const qualificationDate = app.computed_properties.qualification_date;
    const threeMonthsAgo = moment(qualificationDate).subtract(3, "months");
    const inquiriesLast3Months = _.filter(inquiries, (inquiry: any) =>
      moment(inquiry.date).isAfter(threeMonthsAgo),
    );
    for (let i = 0; i < inquiriesLast3Months.length; i += 1) {
      const inquiry = inquiriesLast3Months[i];
      const matchingTradelines = this.findMatchingTradelines(inquiry);
      if (matchingTradelines.length === 0 && inquiry.operator_associated_tradeline_id === null) {
        const inquiryCategory =
          inquiry.operator_credit_business_type || inquiry.credit_business_type;
        if (inquiryCategory === "Automobile") {
          if (!unmatchedAutoInquiry) {
            uniqueInquiryCounter += 1; // all in this category count as 1
            unmatchedAutoInquiry = true;
          }
        } else if (inquiryCategory === "RealEstate") {
          if (!unmatchedRealEstateInquiry) {
            uniqueInquiryCounter += 1; // all in this category count as 1
            unmatchedRealEstateInquiry = true;
          }
        } else {
          // not auto or real estate
          uniqueInquiryCounter += 1;
        }
      }
    }
    if (uniqueInquiryCounter <= 3) {
      return `uniqueInquiryCounter (${uniqueInquiryCounter}) for unmatched inquiries is <= 3`;
    }
    if (
      _.filter(inquiriesLast3Months, (inquiry: any) => {
        const inquiryCategory =
          inquiry.operator_credit_business_type || inquiry.credit_business_type;
        return (
          inquiryCategory !== "ChargeAccount" && inquiryCategory !== "Recreational/StoreCredit"
        );
      }).length < 3
    ) {
      return "Less than 3 inquiries of types other than charge card or store credit";
    }
    return false;
  }

  findMatchingTradelines(inquiry) {
    const recentTradelines = this.findRecentTradelines(inquiry);
    const inquiryCategory = inquiry.operator_credit_business_type || inquiry.credit_business_type;

    const hasMatchingCategory = _.filter(recentTradelines, (tradeline: any) => {
      const tradelineCategory = tradeline.operator_category || tradeline.credit_loan_type;
      return (
        tradelineCategory === inquiryCategory ||
        (tradeline.is_real_estate && inquiryCategory === "RealEstate")
      );
    });

    const hasMatchingCreditor = _.filter(
      recentTradelines,
      (tradeline: any) => tradeline.creditor_name === inquiry.name,
    );

    return hasMatchingCategory.concat(hasMatchingCreditor);
  }

  findRecentTradelines(inquiry) {
    const { tradelines } = this.state;
    const inquiryDate = moment(inquiry.date);
    const threeMonthsLater = moment(inquiry.date).add(3, "months");

    return _.filter(tradelines, (tradeline: any) =>
      moment(tradeline.opened_date).isBetween(inquiryDate, threeMonthsLater),
    );
  }

  static tradelineOptions(tradelines) {
    return (
      tradelines.length > 0 &&
      tradelines.map(tradeline => ({
        label: `${tradeline.creditor_name} -- ${moment(tradeline.opened_date).format("MM-DD-YY")}`,
        value: tradeline.id,
      }))
    );
  }

  showMessageBox(inquiry) {
    const msg = `On ${moment(inquiry.date).format("MMM DD, YYYY")} there was an inquiry \
on your credit report from ${inquiry.name}. Please tell us the reason for seeking new credit.`;
    this.createExplanation(inquiry.id, msg);
  }

  renderInquiries() {
    const { inquiries } = this.state;

    if (this.props.app.credit_reports.length === 0) {
      return (
        <div>
          <h2>No credit report yet</h2>
        </div>
      );
    }

    const inquiryCheckReason = this.inquiryCheckNotRequiredReason();
    return (
      <div className="credit_summary__table_container credit_summary__inquiries">
        <h2>Inquiries</h2>
        <div>
          Inquiry Check Required: {inquiryCheckReason ? `No -- ${inquiryCheckReason}` : "Yes"}
        </div>
        <Table className="credit_summary__inquiries__table">
          <thead>
            <tr>
              <th>Inquiry Made By</th>
              <th>Inquiry Date</th>
              <th>Whose?</th>
              <th>Industry</th>
              <th>Associated Tradeline</th>
            </tr>
          </thead>
          <tbody>
            {inquiries &&
              inquiries.map((inquiry, index) => {
                const matchingTradelines = this.findMatchingTradelines(inquiry);
                const tradelineOptions =
                  CreditSummaryHurdleEditor.tradelineOptions(matchingTradelines);
                return (
                  <tr key={index}>
                    <td>{inquiry.name}</td>
                    <td>{moment.utc(inquiry.date).format("MM-DD-YYYY")}</td>
                    <td>B{new BorrowerEntityUtil(inquiry.borrower_enity).value()}</td>
                    <td>
                      <EditableField
                        inputType="select"
                        inputName={`operator_inquiry_business_type_${index}`}
                        fieldName="operator_credit_business_type"
                        originalValue={inquiry.credit_business_type}
                        changedValue={inquiry.operator_credit_business_type}
                        options={INQUIRY_INDUSTRY_OPTIONS}
                        handleOnChange={this.handleInquiryChange.bind(this, inquiry.id)}
                      />
                    </td>
                    <td>
                      {tradelineOptions && (
                        <EditableField
                          inputType="select"
                          inputName={`operator_inquiry_tradeline_${index}`}
                          fieldName="operator_associated_tradeline_id"
                          originalValue={matchingTradelines[0].id}
                          changedValue={inquiry.operator_associated_tradeline_id}
                          options={tradelineOptions}
                          handleOnChange={this.handleInquiryChange.bind(this, inquiry.id)}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </Table>
      </div>
    );
  }

  renderAddressInformation = residences => (
    <div className="credit_summary__table_container credit_summary__addresses">
      <h2>Address Information</h2>
      <Table className="credit_summary__addresses__table">
        <thead>
          <tr>
            <th>Address</th>
            <th>Address Type</th>
            <th>Applicant</th>
          </tr>
        </thead>
        <tbody>
          {residences &&
            residences.map((residence: CreditReportResidence) => {
              return (
                <tr key={residence.id}>
                  <td>{`${residence.address_line_1}, ${residence.city}, ${residence.state}, ${residence.postal_code}`}</td>
                  <td>{residence.type}</td>
                  <td>{`B${residence.borrower_entity}`}</td>
                </tr>
              );
            })}
        </tbody>
      </Table>
    </div>
  );

  renderOfacResults = results => (
    <div className="credit_summary__table_container credit_summary__ofac_results">
      <h2>OFAC Search Result</h2>
      <Table className="credit_summary__ofac_result__table">
        <thead>
          <tr>
            <th>Status</th>
            <th>Result</th>
            <th>Applicant</th>
          </tr>
        </thead>
        <tbody>
          {!results ? (
            <tr key="result">
              <td colSpan={3}>No Match Found</td>
            </tr>
          ) : (
            results.map((result: OFACCheck, index) => {
              return (
                <tr key={index}>
                  <td>{result.result_status}</td>
                  <td>{result.result_text}</td>
                  <td>{`B${result.borrower_entity}`}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
    </div>
  );

  renderConsumerStatement = consumerStatements => (
    <div className="credit_summary__table_container credit_summary__consumer_statement">
      <h2>Consumer Statement</h2>
      <Table className="credit_summary__consumer_statement__table">
        <thead>
          <tr>
            <th>Message</th>
            <th>Applicant</th>
          </tr>
        </thead>
        <tbody>
          {!consumerStatements ? (
            <tr key="result">
              <td colSpan={3}>None</td>
            </tr>
          ) : (
            consumerStatements.map((statement: ConsumerStatement, index) => {
              return (
                <tr key={index}>
                  <td>{statement.message}</td>
                  <td>{`B${statement.borrower_entity}`}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
    </div>
  );

  renderFraudAlerts = fraudAlerts => (
    <div className="credit_summary__table_container credit_summary__fraud_alerts">
      <h2>Fraud Alert Information</h2>
      <Table className="credit_summary__fraud_alerts__table">
        <thead>
          <tr>
            <th>Message</th>
            <th>Applicant</th>
          </tr>
        </thead>
        <tbody>
          {!fraudAlerts ? (
            <tr key="result">
              <td colSpan={3}>None</td>
            </tr>
          ) : (
            fraudAlerts.map((alert: FraudAlert, index) => {
              return (
                <tr key={index}>
                  <td>{alert.message}</td>
                  <td>{`B${alert.borrower_entity}`}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
    </div>
  );

  renderCreditScoreIdentifiers(borrowers) {
    return (
      <>
        <h2>Credit Report Reference #: </h2>
        <ul>
          {this.props.app.credit_reports.map((creditReport: CreditReport) => {
            return (
              <li key={creditReport.id}>
                {creditReport.credit_report_identifier} -- borrower(s):{" "}
                {renderBorrowerEntity(creditReport.borrower_entity, borrowers)}
              </li>
            );
          })}
        </ul>
      </>
    );
  }

  renderOpenLiabilitiesScreen() {
    return (
      <div className="credit_summary__open_liabilities">
        {this.renderOpenLiabilities()}
        {this.state.showingLoETradelineLinker && this.renderLoELinker()}
        {this.renderAdditionalOpenLiabilities()}
        {this.renderRealEstateOwned()}
        {this.state.showingLoERealEstateLinker && this.renderLoERealEstateLinker()}
        {this.renderPresentHousingExpenses()}
      </div>
    );
  }

  render() {
    const { app, onHide, hurdle, loadApp } = this.props;

    return (
      <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
        <div className={`dashboard-container dashboard-container--${hurdle.name}`}>
          <ButtonToolbar style={{ position: "absolute", top: "14px", left: "270px", zIndex: 12 }}>
            <OrderNewCreditReport
              loanApplicationId={app.id}
              isArchived={app.is_archived}
              loadApp={loadApp}
            />
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="tooltip">
                  Borrower's SSNs are unique in the system. To allow a borrower to apply again with
                  the same SSN we need to unlock the current one.
                </Tooltip>
              }
            >
              <DropdownButton id="ssn" bsSize="small" title="Unlock SSN">
                {[1, 2]
                  .filter(b => Boolean(app[`borrower${b}_first_name`]))
                  .map(borrower => (
                    <MenuItem
                      key={borrower}
                      onClick={() =>
                        APIClient.post(`/uw/loan_applications/${app.id}/ssn/unlock`, {
                          borrower,
                        }).then(() => this.reloadApp())
                      }
                    >
                      {`Borrower ${borrower}`}
                    </MenuItem>
                  ))}
              </DropdownButton>
            </OverlayTrigger>
          </ButtonToolbar>
          {this.renderOpenLiabilitiesScreen()}
          {this.renderCalculatedVariableSummary()}
          {this.renderTradelines()}
          {this.state.showingTradelineLinker && this.renderTradelineLinker()}
          {this.renderInquiries()}
          {app.credit_reports.length > 0 && (
            <>
              {this.renderAddressInformation(
                _.flatten(app.credit_reports.map(cr => cr.residences)),
              )}
              {this.renderOfacResults(_.flatten(app.credit_reports.map(cr => cr.ofac_result)))}
              {this.renderConsumerStatement(
                _.flatten(app.credit_reports.map(cr => cr.consumer_statements)),
              )}
              {this.renderFraudAlerts(_.flatten(app.credit_reports.map(cr => cr.fraud_alerts)))}
            </>
          )}
          {this.renderCreditScoreIdentifiers(this.props.app.borrowers)}
          <footer className={styles.footer}>
            <Button kind="secondary" onClick={onHide}>
              Cancel
            </Button>
            <Button onClick={this.onSave.bind(this)} renderIcon={Save16}>
              Save changes
            </Button>
          </footer>
        </div>
      </HurdleView>
    );
  }
}

export default connect<any, any, any>(null, mapDispatchToProps)(CreditSummaryHurdleEditor);
